import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  fetchActiveOrders,
  fetchActiveOrdersFiltered,
  fetchOrdersByStatus,
  fetchOrderCounts,
  getDateRange,
  updateOrderStatus,
  saveNotification,
  supabase,
  type Order,
} from '@/lib/supabase';
import { refundWalletOnOrderCancel } from '@/hooks/useWallet';
import OrderCard from './components/OrderCard';
import ProductsPanel from './components/ProductsPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import DeliveryBoysPanel from './components/DeliveryBoysPanel';
import DeliveryStatsPanel from './components/DeliveryStatsPanel';
import { useOrderNotification, unlockAudio, useAudioUnlockStatus, hasUserEverUnlockedAudio } from '@/hooks/useOrderNotification';
import { usePushNotification, notifySWOrderActionTaken, notifySWPendingOrders, notifySWStopAllAlarms } from '@/hooks/usePushNotification';
import OutOfAreaPanel from './components/OutOfAreaPanel';
import ComplaintsPanel from './components/ComplaintsPanel';
import LiveDeliveryMap from './components/LiveDeliveryMap';
import CustomerRecordsPanel from './components/CustomerRecordsPanel';
import GiftCodesPanel from './components/GiftCodesPanel';
import {
  getAdminServiceLocation,
  saveAdminServiceLocation,
  calcDistanceKm,
  type AdminServiceLocation,
} from '@/hooks/useLocationCheck';
import AIProductBot from './components/AIProductBot';
import VideoPopupPanel from './components/VideoPopupPanel';
import VisitorActivityPanel from './components/VisitorActivityPanel';
import OrderChatBot from '@/components/feature/OrderChatBot';
import NewOrderAlert from './components/NewOrderAlert';
import RoleManagementPanel from './components/RoleManagementPanel';
import TodaysDealsPanel from './components/TodaysDealsPanel';
import StockAlertsPanel from './components/StockAlertsPanel';
import StockHistoryPanel from './components/StockHistoryPanel';
import HeroBannerPanel from './components/HeroBannerPanel';
import BannerLayoutPanel from './components/BannerLayoutPanel';
import CollectionLayoutPanel from './components/CollectionLayoutPanel';
import HomeLayoutPanel from './components/HomeLayoutPanel';
import ThemePanel from './components/ThemePanel';
import MetaPixelPanel from './components/MetaPixelPanel';
import { logStockChange } from '@/lib/stockHistory';

type ActiveFilter = 'all' | 'pending' | 'confirmed' | 'shipped';
type HistoryTab = 'shipped' | 'delivered' | 'cancelled';
type OrderMode = 'auto' | 'manual';
type MainTab = 'active' | 'history' | 'products' | 'analytics' | 'delivery' | 'deliverystats' | 'livemap' | 'settings' | 'visitors' | 'complaints' | 'customers' | 'giftcodes' | 'videopop' | 'activity' | 'roles' | 'deals' | 'stockalerts' | 'stockhistory' | 'herobanner' | 'bannerlayout' | 'collectionlayout' | 'homelayout' | 'theme' | 'metapixel';
type DatePeriod = 'today' | 'week' | 'month' | 'all';

import { getOrderPauseStatus, saveOrderPauseStatus, PAUSE_REASONS, type OrderPauseStatus } from '@/lib/orderPause';
import { getContactSettings, saveContactSettings, type ContactSettings } from '@/lib/contactSettings';

const PAGE_SIZE = 20;
const QR_STORAGE_KEY = 'company_payment_qr';

const DATE_TABS: { key: DatePeriod; label: string; icon: string }[] = [
  { key: 'today', label: 'Today',      icon: 'ri-sun-line' },
  { key: 'week',  label: 'This Week',  icon: 'ri-calendar-line' },
  { key: 'month', label: 'This Month', icon: 'ri-calendar-2-line' },
  { key: 'all',   label: 'All Time',   icon: 'ri-history-line' },
];

interface AdminUser {
  id: string;
  name: string;
  phone: string;
  email?: string;
  role_id?: string;
  role_name?: string;
  is_active: boolean;
  permissions: string[];
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check login on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('admin_user');
    if (!savedUser) {
      navigate('/admin-login', { replace: true });
      return;
    }
    try {
      const user = JSON.parse(savedUser) as AdminUser;
      if (!user.is_active) {
        localStorage.removeItem('admin_user');
        navigate('/admin-login', { replace: true });
        return;
      }
      setCurrentUser(user);
      setCheckingAuth(false);
      
      // Initialize audio on user interaction after login
      const initAudio = async () => {
        const unlocked = await unlockAudio();
        if (unlocked) {
          console.log('[Admin] Audio unlocked on login');
        }
      };
      // Small delay to ensure page is ready
      setTimeout(initAudio, 500);
    } catch {
      localStorage.removeItem('admin_user');
      navigate('/admin-login', { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_login_time');
    navigate('/admin-login', { replace: true });
  };

  const [datePeriod, setDatePeriod] = useState<DatePeriod>('all');
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState<MainTab>('active');
  const [historyTab, setHistoryTab] = useState<HistoryTab>('delivered');
  const [historyOrders, setHistoryOrders] = useState<Order[]>([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [counts, setCounts] = useState<Record<Order['status'], number>>({
    pending: 0, confirmed: 0, shipped: 0, delivered: 0, cancelled: 0,
  });
  const [periodRevenue, setPeriodRevenue] = useState(0);
  const [search, setSearch] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [orderMode, setOrderMode] = useState<OrderMode>('manual');
  const [pendingAlerts, setPendingAlerts] = useState<Order[]>([]);
  const [showModeInfo, setShowModeInfo] = useState(false);
  const [toastMsg, setToastMsg] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [pauseStatus, setPauseStatus] = useState<OrderPauseStatus>(() => getOrderPauseStatus());
  const [selectedPauseReason, setSelectedPauseReason] = useState(() => getOrderPauseStatus().reason || PAUSE_REASONS[0].id);
  const [customPauseReason, setCustomPauseReason] = useState(() => {
    const saved = getOrderPauseStatus();
    return saved.customReason || '';
  });

  // Contact Settings
  const [contactSettings, setContactSettings] = useState<ContactSettings>(() => getContactSettings());
  const [contactSaved, setContactSaved] = useState(false);
  
  // WhatsApp Admin Notification Settings
  const [adminWhatsAppNumber, setAdminWhatsAppNumber] = useState(() => {
    return localStorage.getItem('admin_whatsapp_number') || '';
  });
  const [whatsAppSaved, setWhatsAppSaved] = useState(false);
  const [lastWhatsAppNotif, setLastWhatsAppNotif] = useState<{orderId: string; time: string; status: 'sent' | 'failed'} | null>(null);

  // Meta Pixel Settings
  const [metaPixelId, setMetaPixelId] = useState(() => {
    return localStorage.getItem('meta_pixel_id') || '';
  });
  const [metaPixelEnabled, setMetaPixelEnabled] = useState(() => {
    return localStorage.getItem('meta_pixel_enabled') === 'true';
  });
  const [metaPixelSaved, setMetaPixelSaved] = useState(false);

  // Custom Head Code Settings
  const [customHeadCode, setCustomHeadCode] = useState(() => {
    return localStorage.getItem('custom_head_code') || '';
  });
  const [customHeadSaved, setCustomHeadSaved] = useState(false);
  const [customHeadPreview, setCustomHeadPreview] = useState(false);
  const [justLoaded, setJustLoaded] = useState(true);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [qrUploading, setQrUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Direct Delivery Toggle (bina OTP ke deliver)
  const [directDeliveryEnabled, setDirectDeliveryEnabled] = useState(() => {
    const saved = localStorage.getItem('direct_delivery_enabled');
    return saved === 'true';
  });

  // ── Service Location State ─────────────────────────────────────────────
  const [adminLoc, setAdminLoc] = useState<AdminServiceLocation | null>(() => getAdminServiceLocation());
  const [locDetecting, setLocDetecting] = useState(false);
  const [locError, setLocError] = useState('');
  const [locMapCoords, setLocMapCoords] = useState<{ lat: number; lng: number } | null>(
    () => {
      const saved = getAdminServiceLocation();
      return saved ? { lat: saved.lat, lng: saved.lng } : null;
    }
  );
  const [locAddress, setLocAddress] = useState('');
  const [locConfirmed, setLocConfirmed] = useState(false);

  // ── Sidebar drawer state ─────────────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── AI Product Bot state ─────────────────────────────────────────────────
  const [showAIBot, setShowAIBot] = useState(false);
  
  // PWA Install prompt state
  const [pwaPrompt, setPwaPrompt] = useState<{ show: boolean; deferredPrompt: Event | null }>({ show: false, deferredPrompt: null });
  const [pwaInstalled, setPwaInstalled] = useState(false);

  const knownOrderIdsRef = useRef<Set<string>>(new Set());
  const orderModeRef = useRef<OrderMode>('manual');
  const { startRinging, stopRinging, triggerOnce } = useOrderNotification();

  const showToast = useCallback((text: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToastMsg({ text, type });
    setTimeout(() => setToastMsg(null), 3500);
  }, []);

  const handleRingFromSW = useCallback((order: Order) => {
    console.log('[Admin] handleRingFromSW called for order:', order.id, 'mode:', orderModeRef.current);
    if (orderModeRef.current === 'manual') {
      setPendingAlerts(prev => {
        const existingIds = new Set(prev.map(o => o.id));
        if (!existingIds.has(order.id)) {
          console.log('[Admin] Adding order to pending alerts:', order.id);
          // Start ringing immediately when adding new order
          setTimeout(() => startRinging(), 100);
          return [...prev, order];
        }
        console.log('[Admin] Order already in pending alerts:', order.id);
        return prev;
      });
    } else {
      console.log('[Admin] Auto mode - ignoring ring from SW');
    }
  }, [startRinging]);

  const { permission, swReady, requestPermission, sendNewOrderNotification } = usePushNotification(handleRingFromSW);
  useEffect(() => { orderModeRef.current = orderMode; }, [orderMode]);

  useEffect(() => {
    const saved = localStorage.getItem(QR_STORAGE_KEY);
    if (saved) setQrImage(saved);

    // Check if PWA is already installed
    if (window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as unknown as { standalone?: boolean }).standalone === true) {
      setPwaInstalled(true);
    }

    // Listen for PWA install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setPwaPrompt({ show: true, deferredPrompt: e });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed
    const handleAppInstalled = () => {
      setPwaPrompt({ show: false, deferredPrompt: null });
      setPwaInstalled(true);
      showToast('App installed successfully! Ab home screen se open karo.', 'success');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [showToast]);

  const loadActiveOrders = useCallback(async (isPolling = false, period?: DatePeriod) => {
    if (!isPolling) setLoading(true);
    const currentPeriod = period ?? datePeriod;
    const range = getDateRange(currentPeriod);

    const [allActive, filteredActive, newCounts] = await Promise.all([
      isPolling ? fetchActiveOrders() : Promise.resolve([] as Order[]),
      fetchActiveOrdersFiltered(range.from, range.to),
      fetchOrderCounts(range.from, range.to),
    ]);

    if (isPolling && knownOrderIdsRef.current.size > 0) {
      const newOrders = allActive.filter(
        o => !knownOrderIdsRef.current.has(o.id) && o.status === 'pending'
      );
      if (newOrders.length > 0) {
        // IMMEDIATE: Play sound right away for new orders
        if (orderModeRef.current === 'auto') {
          for (const order of newOrders) {
            // Deduct stock when auto-confirming order (await for proper sync)
            await deductStock(order.items);
            await updateOrderStatus(order.id, 'confirmed');
            await saveNotification(order, 'order_confirmed');
          }
          triggerOnce();
          showToast(`${newOrders.length} order(s) auto-accepted!`, 'success');
          const refreshed = await fetchActiveOrdersFiltered(range.from, range.to);
          refreshed.forEach(o => knownOrderIdsRef.current.add(o.id));
          setActiveOrders(refreshed);
          setLastRefresh(new Date());
          if (!isPolling) setLoading(false);
          return;
        } else {
          // IMMEDIATE: Add to pending and start ringing right away
          console.log('[Admin] New orders in manual mode:', newOrders.length);
          setPendingAlerts(prev => {
            const existingIds = new Set(prev.map(o => o.id));
            const toAdd = newOrders.filter(o => !existingIds.has(o.id));
            if (toAdd.length > 0) {
              console.log('[Admin] Adding to pending alerts:', toAdd.map(o => o.id));
              // Start ringing immediately
              setTimeout(() => startRinging(), 100);
            }
            return [...prev, ...toAdd];
          });
          for (const order of newOrders) {
            // Push notification (handles screen lock alarm via SW)
            console.log('[Admin] Sending push notification for order:', order.id);
            sendNewOrderNotification(order);
          }
        }
      }
      allActive.forEach(o => knownOrderIdsRef.current.add(o.id));
    } else {
      filteredActive.forEach(o => knownOrderIdsRef.current.add(o.id));
    }

    setActiveOrders(filteredActive);
    setCounts(newCounts);
    setLastRefresh(new Date());
    if (!isPolling) setLoading(false);
  }, [datePeriod, startRinging, triggerOnce, showToast, sendNewOrderNotification]);

  const loadHistory = useCallback(async (
    tab: HistoryTab,
    page: number,
    append = false,
    period?: DatePeriod
  ) => {
    if (page === 0) setHistoryLoading(true);
    else setHistoryLoadingMore(true);

    const currentPeriod = period ?? datePeriod;
    const range = getDateRange(currentPeriod);
    const result = await fetchOrdersByStatus(tab, page, PAGE_SIZE, range.from, range.to);

    if (tab === 'delivered' && page === 0) {
      const revenue = result.data.reduce((s, o) => s + o.final_amount, 0);
      setPeriodRevenue(revenue);
    }

    if (append) {
      setHistoryOrders(prev => {
        const updated = [...prev, ...result.data];
        if (tab === 'delivered') {
          setPeriodRevenue(updated.reduce((s, o) => s + o.final_amount, 0));
        }
        return updated;
      });
    } else {
      setHistoryOrders(result.data);
    }

    setHistoryHasMore(result.hasMore);
    setHistoryTotal(result.total);
    setHistoryPage(page);
    setHistoryLoading(false);
    setHistoryLoadingMore(false);
  }, [datePeriod]);

  const handleDateChange = useCallback((period: DatePeriod) => {
    setDatePeriod(period);
    setSearch('');
    loadActiveOrders(false, period);
    if (mainTab === 'history') {
      setHistoryOrders([]);
      setHistoryPage(0);
      loadHistory(historyTab, 0, false, period);
    }
  }, [mainTab, historyTab, loadActiveOrders, loadHistory]);

  useEffect(() => { loadActiveOrders(false); }, []);

  const loadActiveOrdersRef = useRef(loadActiveOrders);
  useEffect(() => { loadActiveOrdersRef.current = loadActiveOrders; }, [loadActiveOrders]);

  useEffect(() => {
    const interval = setInterval(() => loadActiveOrdersRef.current(true), 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (mainTab === 'history') {
      setHistoryOrders([]);
      setHistoryPage(0);
      loadHistory(historyTab, 0, false);
    }
  }, [mainTab, historyTab]);

  useEffect(() => {
    if (pendingAlerts.length === 0) {
      stopRinging();
    } else {
      startRinging();
    }
  }, [pendingAlerts.length, stopRinging, startRinging]);

  // Handle pending orders on load - runs when data is first loaded
  useEffect(() => {
    // Only run once after initial data load completes
    if (!justLoaded) return;
    
    // Wait for loading to complete
    if (loading) {
      console.log('[Admin] Still loading, waiting...');
      return;
    }
    
    // Loading complete - check for pending orders
    const pendingOrders = activeOrders.filter(o => o.status === 'pending');
    console.log('[Admin] Loading complete. Checking pending orders:', pendingOrders.length, 'pending out of', activeOrders.length, 'total');
    
    if (pendingOrders.length > 0 && orderMode === 'manual') {
      console.log('[Admin] Starting ring for', pendingOrders.length, 'pending orders');
      setPendingAlerts(prev => {
        const existingIds = new Set(prev.map(o => o.id));
        const toAdd = pendingOrders.filter(o => !existingIds.has(o.id));
        if (toAdd.length > 0) {
          console.log('[Admin] Adding', toAdd.length, 'orders to pending alerts');
          // Small delay to ensure audio context is ready
          setTimeout(() => startRinging(), 300);
          return [...prev, ...toAdd];
        }
        return prev;
      });
      // Tell SW about pending orders so alarm works on screen lock
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(() => {
          console.log('[Admin] Notifying SW about pending orders');
          notifySWPendingOrders(pendingOrders);
        }).catch(() => {});
      }
    } else if (pendingOrders.length === 0) {
      // No pending orders — stop all SW alarms
      notifySWStopAllAlarms();
    }
    
    // Mark as processed
    setJustLoaded(false);
  }, [justLoaded, loading, activeOrders, orderMode, startRinging]);

  // Close sidebar on body scroll lock
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Deduct stock from localStorage AND Supabase when order is confirmed
  const deductStock = useCallback(async (items: Order['items']) => {
    try {
      // First update localStorage
      const stored = localStorage.getItem('listed_products');
      if (stored) {
        const listed = JSON.parse(stored);
        if (Array.isArray(listed)) {
          const updated = listed.map((p: { id: number; name?: string; price?: number; stock?: number; outOfStock?: boolean; stockBreakdown?: Record<string, unknown>; colors?: { name: string }[]; sizes?: string[] }) => {
            const orderItem = items.find((i: Order['items'][0]) => String(i.id) === String(p.id) || (p.name === i.name && p.price === i.price));
            if (!orderItem) return p;

            let newBreakdown = { ...(p.stockBreakdown ?? {}) };
            const hasColors = (p.colors?.length ?? 0) > 0;
            const hasSizes = (p.sizes?.length ?? 0) > 0;
            const colorName = orderItem.selectedColor?.name;
            const sizeName = orderItem.size;
            const qty = orderItem.quantity;

            if (hasColors && hasSizes && colorName && sizeName) {
              const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
              const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
              newBreakdown[colorName] = { ...colorMap, [sizeName]: Math.max(0, prev - qty) };
            } else if (hasColors && !hasSizes && colorName) {
              const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
              newBreakdown[colorName] = Math.max(0, prev - qty);
            } else if (!hasColors && hasSizes && sizeName) {
              const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
              newBreakdown[sizeName] = Math.max(0, prev - qty);
            } else {
              const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (p.stock ?? 0);
              newBreakdown['total'] = Math.max(0, prev - qty);
            }

            let newTotal = 0;
            Object.values(newBreakdown).forEach(v => {
              if (typeof v === 'number') newTotal += v;
              else if (typeof v === 'object' && v !== null) {
                Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
              }
            });

            return { ...p, stock: newTotal, outOfStock: newTotal === 0, stockBreakdown: newBreakdown };
          });
          localStorage.setItem('listed_products', JSON.stringify(updated));
        }
      }

      // Also update Supabase for each item
      for (const item of items) {
        const { data: matchedProducts } = await supabase
          .from('products')
          .select('id, stock_breakdown, stock, name, price')
          .eq('name', item.name)
          .eq('price', item.price)
          .eq('status', 'approved')
          .limit(1);

        if (matchedProducts && matchedProducts.length > 0) {
          const product = matchedProducts[0];
          const currentBreakdown = (product.stock_breakdown as Record<string, unknown>) ?? {};
          let newBreakdown = { ...currentBreakdown };
          const qty = item.quantity;
          const colorName = item.selectedColor?.name;
          const sizeName = item.size;

          if (colorName && sizeName) {
            const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
            const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
            newBreakdown[colorName] = { ...colorMap, [sizeName]: Math.max(0, prev - qty) };
          } else if (colorName) {
            const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
            newBreakdown[colorName] = Math.max(0, prev - qty);
          } else if (sizeName) {
            const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
            newBreakdown[sizeName] = Math.max(0, prev - qty);
          } else {
            const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (product.stock ?? 0);
            newBreakdown['total'] = Math.max(0, prev - qty);
          }

          let newTotal = 0;
          Object.values(newBreakdown).forEach(v => {
            if (typeof v === 'number') newTotal += v;
            else if (typeof v === 'object' && v !== null) {
              Object.values(v as Record<string, number>).forEach(sv => { if (typeof sv === 'number') newTotal += sv; });
            }
          });

          const stockBefore = product.stock ?? 0;
          await supabase
            .from('products')
            .update({ stock: newTotal, stock_breakdown: newBreakdown, out_of_stock: newTotal === 0 })
            .eq('id', product.id);

          // Log stock change to history
          const variantLabel = colorName && sizeName
            ? `${colorName} / ${sizeName}`
            : colorName || sizeName || undefined;
          await logStockChange({
            product_id: product.id,
            product_name: item.name,
            category: '',
            change_type: 'deduct',
            change_amount: qty,
            stock_before: stockBefore,
            stock_after: newTotal,
            variant_label: variantLabel,
            note: 'Order confirm pe auto deduct',
            performed_by: 'system',
          });
        }
      }
    } catch (e) {
      console.error('Stock deduct failed:', e);
    }
  }, []);

  const handleAccept = useCallback(async (order: Order) => {
    stopRinging();
    // Tell SW to stop alarm for this order
    notifySWOrderActionTaken(order.id);
    setPendingAlerts(prev => prev.filter(o => o.id !== order.id));
    // Deduct stock when confirming order (localStorage + Supabase)
    await deductStock(order.items);
    const result = await updateOrderStatus(order.id, 'confirmed');
    if (result.success) {
      await saveNotification(order, 'order_confirmed');
      setActiveOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: 'confirmed' } : o));
      showToast('Order accepted! Customer notified.', 'success');
    } else {
      showToast('Failed to accept order. Try again.', 'error');
    }
  }, [stopRinging, showToast, deductStock]);

  // Restore stock to localStorage when order is cancelled/rejected
  const restoreStock = useCallback(async (items: Order['items']) => {
    try {
      const stored = localStorage.getItem('listed_products');
      if (!stored) return;
      const listed = JSON.parse(stored);
      if (!Array.isArray(listed)) return;

      const updated = listed.map((p: { id: number; name?: string; price?: number; stock?: number; outOfStock?: boolean; stockBreakdown?: Record<string, unknown>; colors?: { name: string }[]; sizes?: string[] }) => {
        // Fix: Match by ID OR by name+price (same as deductStock)
        const orderItem = items.find((i: Order['items'][0]) => String(i.id) === String(p.id) || (p.name === i.name && p.price === i.price));
        if (!orderItem) return p;

        // Restore stock
        const newStock = (p.stock ?? 0) + orderItem.quantity;

        // Restore stockBreakdown if exists - SAME LOGIC AS DEDUCT
        let newBreakdown = p.stockBreakdown ? { ...p.stockBreakdown } : undefined;
        if (newBreakdown) {
          const colorName = orderItem.selectedColor?.name;
          const sizeName = orderItem.size;
          const qty = orderItem.quantity;
          
          const hasColors = (p.colors?.length ?? 0) > 0;
          const hasSizes = (p.sizes?.length ?? 0) > 0;

          if (hasColors && hasSizes && colorName && sizeName) {
            // Color + Size matrix
            const colorMap = (newBreakdown[colorName] as Record<string, number>) ?? {};
            const prev = typeof colorMap[sizeName] === 'number' ? colorMap[sizeName] : 0;
            (newBreakdown[colorName] as Record<string, number>) = { ...colorMap, [sizeName]: prev + qty };
          } else if (hasColors && !hasSizes && colorName) {
            // Color only
            const prev = typeof newBreakdown[colorName] === 'number' ? (newBreakdown[colorName] as number) : 0;
            newBreakdown[colorName] = prev + qty;
          } else if (!hasColors && hasSizes && sizeName) {
            // Size only
            const prev = typeof newBreakdown[sizeName] === 'number' ? (newBreakdown[sizeName] as number) : 0;
            newBreakdown[sizeName] = prev + qty;
          } else {
            // Total only
            const prev = typeof newBreakdown['total'] === 'number' ? (newBreakdown['total'] as number) : (p.stock ?? 0);
            newBreakdown['total'] = prev + qty;
          }
        }

        return {
          ...p,
          stock: newStock,
          outOfStock: newStock === 0,
          stockBreakdown: newBreakdown,
        };
      });

      localStorage.setItem('listed_products', JSON.stringify(updated));
      console.log('[Admin] Stock restored for', items.length, 'items');

      // Also restore in Supabase + log history
      for (const item of items) {
        try {
          const { data: matchedProducts } = await supabase
            .from('products')
            .select('id, stock, name, price, category')
            .eq('name', item.name)
            .eq('price', item.price)
            .eq('status', 'approved')
            .limit(1);

          if (matchedProducts && matchedProducts.length > 0) {
            const product = matchedProducts[0];
            const stockBefore = product.stock ?? 0;
            const newTotal = stockBefore + item.quantity;
            await supabase
              .from('products')
              .update({ stock: newTotal, out_of_stock: false })
              .eq('id', product.id);

            const variantLabel = item.selectedColor?.name && item.size
              ? `${item.selectedColor.name} / ${item.size}`
              : item.selectedColor?.name || item.size || undefined;
            await logStockChange({
              product_id: product.id,
              product_name: item.name,
              category: product.category ?? '',
              change_type: 'restore',
              change_amount: item.quantity,
              stock_before: stockBefore,
              stock_after: newTotal,
              variant_label: variantLabel,
              note: 'Order cancel/reject pe stock restore',
              performed_by: 'system',
            });
          }
        } catch { /* ignore per-item errors */ }
      }
    } catch (e) {
      console.error('Stock restore failed:', e);
    }
  }, []);

  const handleReject = useCallback(async (order: Order, reason: string) => {
    console.log('[Admin] Rejecting order:', order.id, 'Current status:', order.status);
    stopRinging();
    // Tell SW to stop alarm for this order
    notifySWOrderActionTaken(order.id);
    setPendingAlerts(prev => prev.filter(o => o.id !== order.id));
    // Only restore stock if order was confirmed/shipped (stock was actually deducted)
    // If order was pending, stock was never deducted, so don't restore
    if (order.status === 'confirmed' || order.status === 'shipped') {
      console.log('[Admin] Restoring stock for confirmed/shipped order');
      restoreStock(order.items);
    } else {
      console.log('[Admin] No stock to restore - order was pending');
    }
    const result = await updateOrderStatus(order.id, 'cancelled');
    if (result.success) {
      await saveNotification(order, 'order_cancelled', reason);
      setActiveOrders(prev => prev.filter(o => o.id !== order.id));
      setCounts(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1), cancelled: prev.cancelled + 1 }));
      try {
        const stored = localStorage.getItem('currentOrder');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed.id === order.id) localStorage.removeItem('currentOrder');
        }
      } catch { /* ignore */ }

      // ── Wallet Refund on Rejection ────────────────────────────────────────
      // Refund wallet_discount back to customer
      // Reverse 10% cashback only if order was confirmed/shipped (cashback was already credited)
      const walletDiscount = order.wallet_discount ?? 0;
      const cashbackToReverse = (order.status === 'confirmed' || order.status === 'shipped')
        ? Math.floor(order.final_amount * 0.1)
        : 0;

      console.log('[Admin Reject] Order:', order.id, 'Status:', order.status, 'Wallet Discount:', walletDiscount, 'Cashback to Reverse:', cashbackToReverse);

      if (walletDiscount > 0 || cashbackToReverse > 0) {
        refundWalletOnOrderCancel(
          order.customer_phone,
          walletDiscount,
          cashbackToReverse,
          order.id
        ).then(success => {
          console.log('[Admin Reject] Wallet refund result:', success);
        }).catch(err => {
          console.error('[Admin Reject] Wallet refund error:', err);
        });
      } else {
        console.log('[Admin Reject] No wallet refund needed');
      }

      showToast('Order rejected. Customer notified.', 'error');
    } else {
      showToast('Failed to reject order. Try again.', 'error');
    }
  }, [stopRinging, showToast, restoreStock]);

  const handleStatusChange = useCallback((orderId: string, newStatus: Order['status']) => {
    if (newStatus === 'delivered' || newStatus === 'cancelled') {
      setActiveOrders(prev => prev.filter(o => o.id !== orderId));
      setCounts(prev => {
        const order = activeOrders.find(o => o.id === orderId);
        if (!order) return prev;
        return {
          ...prev,
          [order.status]: Math.max(0, prev[order.status] - 1),
          [newStatus]: prev[newStatus] + 1,
        };
      });
    } else {
      setActiveOrders(prev =>
        prev.map(o => o.id === orderId ? { ...o, status: newStatus, updated_at: new Date().toISOString() } : o)
      );
    }
  }, [activeOrders]);

  const filteredActive = activeOrders.filter(o => {
    const matchStatus = activeFilter === 'all' || o.status === activeFilter;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      o.customer_name.toLowerCase().includes(q) ||
      o.customer_phone.includes(q) ||
      o.id.toLowerCase().includes(q) ||
      o.customer_city.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  const filteredHistory = historyOrders.filter(o => {
    const q = search.toLowerCase();
    return !q ||
      o.customer_name.toLowerCase().includes(q) ||
      o.customer_phone.includes(q) ||
      o.id.toLowerCase().includes(q) ||
      o.customer_city.toLowerCase().includes(q);
  });

  const totalOrders = counts.pending + counts.confirmed + counts.shipped + counts.delivered + counts.cancelled;

  const ACTIVE_TABS: { key: ActiveFilter; label: string; icon: string }[] = [
    { key: 'all',       label: 'All Active',  icon: 'ri-list-check' },
    { key: 'pending',   label: 'Pending',     icon: 'ri-time-line' },
    { key: 'confirmed', label: 'Confirmed',   icon: 'ri-checkbox-circle-line' },
    { key: 'shipped',   label: 'Shipped',     icon: 'ri-truck-line' },
  ];

  const periodLabel = DATE_TABS.find(d => d.key === datePeriod)?.label ?? '';

  const audioUnlocked = useAudioUnlockStatus();
  const [audioUnlockDismissed, setAudioUnlockDismissed] = useState(() => hasUserEverUnlockedAudio());

  const handleQrUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Sirf image file upload karo', 'error'); return; }
    if (file.size > 2 * 1024 * 1024) { showToast('File size 2MB se zyada nahi honi chahiye', 'error'); return; }
    setQrUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        localStorage.setItem(QR_STORAGE_KEY, base64);
        setQrImage(base64);
        setQrUploading(false);
        showToast('QR Code upload ho gaya!', 'success');
      };
      reader.onerror = () => { setQrUploading(false); showToast('Upload failed.', 'error'); };
      reader.readAsDataURL(file);
    } catch { setQrUploading(false); showToast('Kuch galat ho gaya', 'error'); }
  };

  const handleRemoveQr = () => {
    localStorage.removeItem(QR_STORAGE_KEY);
    setQrImage(null);
    showToast('QR Code hata diya gaya', 'info');
  };

  const handlePageInteraction = useCallback(async () => {
    if (!audioUnlocked) await unlockAudio();
  }, [audioUnlocked]);

  const handleTogglePause = useCallback((shouldPause: boolean) => {
    const reasonObj = PAUSE_REASONS.find(r => r.id === selectedPauseReason) ?? PAUSE_REASONS[0];
    const isCustom = selectedPauseReason === 'custom';
    const displayLabel = isCustom && customPauseReason.trim()
      ? customPauseReason.trim()
      : reasonObj.label;
    const newStatus: OrderPauseStatus = {
      paused: shouldPause,
      reason: shouldPause ? reasonObj.id : '',
      customReason: shouldPause && isCustom ? customPauseReason.trim() : '',
      pausedAt: shouldPause ? Date.now() : null,
    };
    saveOrderPauseStatus(newStatus);
    setPauseStatus(newStatus);
    showToast(
      shouldPause
        ? `Orders paused — "${displayLabel}"`
        : 'Orders resumed! Customers can now place orders.',
      shouldPause ? 'error' : 'success'
    );
  }, [selectedPauseReason, customPauseReason, showToast]);

  // ── Admin Location Handlers ───────────────────────────────────────────
  const handleDetectAdminLocation = useCallback(() => {
    setLocError('');
    setLocConfirmed(false);
    if (!navigator.geolocation) {
      setLocError('Geolocation is not supported by your browser.');
      return;
    }
    setLocDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setLocMapCoords({ lat, lng });
        let address = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await res.json();
          address = data.display_name || address;
        } catch { /* ignore */ }
        setLocAddress(address);
        setLocDetecting(false);
      },
      (err) => {
        setLocDetecting(false);
        setLocError(
          err.code === 1
            ? 'Location permission denied. Please allow location access.'
            : 'Unable to detect location. Please try again.'
        );
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  const handleSaveAdminLocation = useCallback(() => {
    if (!locMapCoords) return;
    const loc: Omit<AdminServiceLocation, 'setAt'> = {
      lat: locMapCoords.lat,
      lng: locMapCoords.lng,
      address: locAddress || `${locMapCoords.lat.toFixed(5)}, ${locMapCoords.lng.toFixed(5)}`,
    };
    saveAdminServiceLocation(loc);
    setAdminLoc({ ...loc, setAt: Date.now() });
    setLocConfirmed(true);
    showToast('Service location saved! Customers will be checked against this location.', 'success');
  }, [locMapCoords, locAddress, showToast]);

  const MENU_ITEMS: { key: MainTab; label: string; icon: string; desc: string; activeColor: string; activeBg: string }[] = [
    { key: 'active',    label: 'Active Orders', icon: 'ri-flashlight-line',   desc: 'Live incoming orders',    activeColor: 'text-white', activeBg: 'bg-gray-900' },
    { key: 'history',   label: 'History',       icon: 'ri-history-line',      desc: 'Delivered & cancelled',   activeColor: 'text-white', activeBg: 'bg-gray-700' },
    { key: 'products',  label: 'Products',      icon: 'ri-shopping-bag-line', desc: 'Manage your products',    activeColor: 'text-white', activeBg: 'bg-orange-500' },
    { key: 'analytics', label: 'Analytics',     icon: 'ri-bar-chart-2-line',  desc: 'Sales & revenue stats',   activeColor: 'text-white', activeBg: 'bg-green-600' },
    { key: 'delivery',  label: 'Delivery Boys', icon: 'ri-e-bike-2-line',     desc: 'Manage delivery accounts',activeColor: 'text-white', activeBg: 'bg-gray-800' },
    { key: 'deliverystats', label: 'Delivery Stats', icon: 'ri-bar-chart-box-line', desc: 'Per boy performance', activeColor: 'text-white', activeBg: 'bg-gray-900' },
    { key: 'livemap',   label: 'Live Map',      icon: 'ri-map-2-line',        desc: 'Real-time delivery tracking', activeColor: 'text-white', activeBg: 'bg-green-700' },
    { key: 'visitors',  label: 'Area Visitors', icon: 'ri-map-pin-line',      desc: 'Out-of-area customers',   activeColor: 'text-white', activeBg: 'bg-orange-600' },
    { key: 'customers',  label: 'Customers',     icon: 'ri-team-line',         desc: 'Customer records & bonus', activeColor: 'text-white', activeBg: 'bg-teal-600' },
    { key: 'giftcodes',  label: 'Gift Codes',    icon: 'ri-coupon-3-line',     desc: 'Discount & promo codes',  activeColor: 'text-white', activeBg: 'bg-pink-600' },
    { key: 'complaints', label: 'Complaints',    icon: 'ri-customer-service-2-line', desc: 'Customer complaints', activeColor: 'text-white', activeBg: 'bg-red-600' },
    { key: 'activity',   label: 'Visitor Activity', icon: 'ri-eye-line',        desc: 'Website visitor tracking', activeColor: 'text-white', activeBg: 'bg-cyan-600' },
    { key: 'videopop',  label: 'Video Popup',   icon: 'ri-video-line',        desc: 'Promotional video popup', activeColor: 'text-white', activeBg: 'bg-rose-600' },
    { key: 'settings',  label: 'Settings',      icon: 'ri-settings-3-line',   desc: 'QR code & preferences',   activeColor: 'text-white', activeBg: 'bg-gray-600' },
    { key: 'roles',     label: 'Role Management', icon: 'ri-shield-user-line',  desc: 'Staff roles & permissions', activeColor: 'text-white', activeBg: 'bg-violet-600' },
    { key: 'deals',       label: "Today's Deals",   icon: 'ri-fire-fill',              desc: 'Flash deals manage karo',      activeColor: 'text-white', activeBg: 'bg-orange-500' },
    { key: 'stockalerts',  label: 'Stock Alerts',   icon: 'ri-alarm-warning-line',     desc: 'Low stock notifications',      activeColor: 'text-white', activeBg: 'bg-red-500' },
    { key: 'stockhistory', label: 'Stock History',  icon: 'ri-history-line',           desc: 'Stock change log & tracking',  activeColor: 'text-white', activeBg: 'bg-indigo-600' },
    { key: 'herobanner',       label: 'Home Banners',      icon: 'ri-image-edit-line',    desc: 'Hero section images manage karo',    activeColor: 'text-white', activeBg: 'bg-orange-500' },
    { key: 'bannerlayout',     label: 'Banner Layout',     icon: 'ri-layout-masonry-line', desc: '10 premium banner styles choose karo', activeColor: 'text-white', activeBg: 'bg-amber-500' },
    { key: 'collectionlayout', label: 'Collection Design',  icon: 'ri-layout-4-line',      desc: 'Product grid layout choose karo',    activeColor: 'text-white', activeBg: 'bg-rose-500' },
    { key: 'homelayout',       label: 'Home Page Layout', icon: 'ri-layout-3-line',      desc: 'Sections ka order control karo',     activeColor: 'text-white', activeBg: 'bg-violet-500' },
    { key: 'theme',            label: 'Website Theme',    icon: 'ri-palette-line',       desc: '20 premium themes choose karo',     activeColor: 'text-white', activeBg: 'bg-pink-500' },
    { key: 'metapixel',        label: 'Meta Pixel',       icon: 'ri-facebook-circle-fill', desc: 'Facebook/Instagram ads tracking',  activeColor: 'text-white', activeBg: 'bg-blue-600' },
  ];

  const [lowStockCount, setLowStockCount] = useState(0);

  // Load low stock count for badge
  useEffect(() => {
    const loadLowStockCount = async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('stock')
          .eq('status', 'approved')
          .lte('stock', 10);
        setLowStockCount(data?.length ?? 0);
      } catch { /* ignore */ }
    };
    loadLowStockCount();
    const interval = setInterval(loadLowStockCount, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const getTabBadge = (key: MainTab) => {
    if (key === 'active' && activeOrders.length > 0) return activeOrders.length;
    if (key === 'history') return counts.delivered + counts.cancelled;
    if (key === 'livemap') return counts.shipped > 0 ? counts.shipped : null;
    if (key === 'stockalerts') return lowStockCount > 0 ? lowStockCount : null;
    return null;
  };

  const currentMenuItem = MENU_ITEMS.find(m => m.key === mainTab)!;

  const handleMenuSelect = (key: MainTab) => {
    setMainTab(key);
    setSidebarOpen(false);
  };

  return (
    <div
      className="min-h-screen bg-slate-50"
      onClick={handlePageInteraction}
      onTouchStart={handlePageInteraction}
    >
      {/* PWA Install Banner — Premium Glassmorphism */}
      {pwaPrompt.show && !pwaInstalled && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white px-4 py-3 flex items-center justify-between gap-3 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-white/20 rounded-xl backdrop-blur-sm">
              <i className="ri-download-cloud-line text-xl"></i>
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm leading-tight">Install ClothyBazar Admin</p>
              <p className="text-white/80 text-xs leading-relaxed">
                Better notifications + home screen access
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              className="bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-all"
              onClick={() => setPwaPrompt({ show: false, deferredPrompt: null })}
            >
              Later
            </button>
            <button
              className="bg-white text-indigo-600 rounded-lg px-4 py-1.5 text-xs font-bold whitespace-nowrap shadow-lg hover:shadow-xl transition-all"
              onClick={async () => {
                if (pwaPrompt.deferredPrompt) {
                  // @ts-expect-error - beforeinstallprompt event
                  await pwaPrompt.deferredPrompt.prompt();
                  // @ts-expect-error - beforeinstallprompt event
                  const { outcome } = await pwaPrompt.deferredPrompt.userChoice;
                  if (outcome === 'accepted') {
                    setPwaPrompt({ show: false, deferredPrompt: null });
                    setPwaInstalled(true);
                  }
                }
              }}
            >
              Install
            </button>
          </div>
        </div>
      )}

      {/* Audio Unlock Banner — Premium Amber */}
      {!audioUnlocked && !audioUnlockDismissed && (
        <div
          className={`fixed left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center justify-between gap-3 cursor-pointer shadow-lg ${pwaPrompt.show && !pwaInstalled ? 'top-[60px]' : 'top-0'}`}
          onClick={async (e) => {
            e.stopPropagation();
            const ok = await unlockAudio();
            if (ok) setAudioUnlockDismissed(true);
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-white/20 rounded-xl backdrop-blur-sm">
              <i className="ri-volume-up-line text-xl"></i>
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm leading-tight">Enable Order Ring Sound</p>
              <p className="text-white/80 text-xs leading-tight">Tap to activate audio notifications</p>
            </div>
          </div>
          <button
            className="shrink-0 bg-white text-amber-600 rounded-lg px-4 py-1.5 text-xs font-bold whitespace-nowrap shadow-lg hover:shadow-xl transition-all"
            onClick={async (e) => {
              e.stopPropagation();
              const ok = await unlockAudio();
              if (ok) setAudioUnlockDismissed(true);
            }}
          >
            Enable
          </button>
        </div>
      )}

      {/* AI Product Bot */}
      {showAIBot && (
        <AIProductBot
          onProductAdded={() => {
            // Force refresh products panel by switching tabs
            showToast('Product added! Refreshing products...', 'success');
            // Dispatch custom event to refresh products panel
            window.dispatchEvent(new CustomEvent('ai-product-added'));
          }}
          onClose={() => setShowAIBot(false)}
        />
      )}

      {/* Floating AI Bot Button — Mobile */}
      {!showAIBot && (
        <button
          onClick={() => setShowAIBot(true)}
          className="fixed bottom-6 right-4 z-40 lg:hidden flex items-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-bold shadow-xl shadow-orange-500/30 hover:shadow-2xl transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-robot-2-line text-lg"></i>
          AI Bot
        </button>
      )}

      {/* Order ChatBot — background chat assistant */}
      <OrderChatBot
        externalOrder={null}
        onAccept={handleAccept}
        onReject={(order, reason) => handleReject(order, reason)}
        requirePermission={false}
      />

      {/* New Order Alert Popup — shows for each pending order one by one */}
      {pendingAlerts.length > 0 && orderMode === 'manual' && (
        <NewOrderAlert
          order={pendingAlerts[0]}
          onAccept={handleAccept}
          onReject={handleReject}
          onSilence={stopRinging}
        />
      )}



      {/* Toast — Premium Glassmorphism */}
      {toastMsg && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3.5 rounded-2xl text-white text-sm font-semibold flex items-center gap-3 whitespace-nowrap shadow-2xl backdrop-blur-md ${
          toastMsg.type === 'success' ? 'bg-emerald-500/95' :
          toastMsg.type === 'error' ? 'bg-rose-500/95' : 'bg-slate-800/95'
        }`}>
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
            <i className={toastMsg.type === 'success' ? 'ri-check-line' : toastMsg.type === 'error' ? 'ri-close-line' : 'ri-information-line'}></i>
          </div>
          {toastMsg.text}
        </div>
      )}

      {/* ── MOBILE SIDEBAR DRAWER — Premium Glassmorphism ── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Drawer Panel — Premium Dark Theme */}
      <div
        className={`fixed top-0 left-0 h-full w-80 z-50 bg-slate-900 flex flex-col transition-transform duration-300 ease-in-out lg:hidden shadow-2xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Drawer Header — Premium Gradient */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
              <i className="ri-shield-star-line text-white text-sm"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm leading-none">Admin Panel</p>
              <p className="text-slate-400 text-[11px] mt-0.5">ClothyBazar Dashboard</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 hover:bg-slate-700 hover:text-white transition-all cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>

        {/* Menu Items — Premium Dark Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider px-3 pb-2">Main Navigation</p>
          {MENU_ITEMS.map(item => {
            const badge = getTabBadge(item.key);
            const isActive = mainTab === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleMenuSelect(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer text-left group ${
                  isActive
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                  isActive ? 'bg-white/20' : 'bg-slate-800 group-hover:bg-slate-700'
                }`}>
                  <i className={`${item.icon} text-sm ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="leading-none">{item.label}</p>
                </div>
                {badge !== null && badge > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${
                    isActive ? 'bg-white/25 text-white' : 'bg-rose-500 text-white'
                  }`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Quick Stats in Drawer — Premium Cards */}
        <div className="px-3 py-3 border-t border-slate-800 bg-slate-900/50">
          <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider px-3 mb-2">Live Stats</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-2 text-center border border-amber-500/20">
              <p className="text-amber-400 font-bold text-lg leading-none">{counts.pending}</p>
              <p className="text-amber-500/70 text-[10px] mt-0.5">Pending</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl p-2 text-center border border-emerald-500/20">
              <p className="text-emerald-400 font-bold text-lg leading-none">{counts.delivered}</p>
              <p className="text-emerald-500/70 text-[10px] mt-0.5">Delivered</p>
            </div>
            <div className="bg-gradient-to-br from-rose-500/20 to-red-500/20 rounded-xl p-2 text-center border border-rose-500/20">
              <p className="text-rose-400 font-bold text-lg leading-none">{counts.cancelled}</p>
              <p className="text-rose-500/70 text-[10px] mt-0.5">Cancelled</p>
            </div>
          </div>
          <div className="flex items-center justify-between bg-slate-800 rounded-xl px-3 py-2 mt-2 border border-slate-700">
            <span className="text-slate-400 text-xs flex items-center gap-1.5">
              <i className="ri-money-rupee-circle-line text-emerald-400 text-sm"></i>Revenue
            </span>
            <span className="text-emerald-400 font-bold text-sm">₹{periodRevenue.toLocaleString('en-IN')}</span>
          </div>
        </div>

        {/* AI Bot Button in Drawer */}
        <div className="px-3 pb-2">
          <button
            onClick={() => { setSidebarOpen(false); setShowAIBot(true); }}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-robot-2-line"></i>
            AI Product Bot
          </button>
        </div>

        {/* Store Link — Premium Gradient Button */}
        <div className="px-3 pb-4">
          <Link
            to="/"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold hover:shadow-lg hover:shadow-purple-500/25 transition-all"
          >
            <i className="ri-store-2-line"></i>
            Go to Store
          </Link>
        </div>
      </div>

      {/* Top Nav — Premium Dark Header */}
      <header className={`bg-slate-900 border-b border-slate-800 sticky z-30 shadow-lg shadow-slate-900/20 ${
        (pwaPrompt.show && !pwaInstalled) && (!audioUnlocked && !audioUnlockDismissed) 
          ? 'top-[120px]' 
          : (pwaPrompt.show && !pwaInstalled) || (!audioUnlocked && !audioUnlockDismissed) 
            ? 'top-[60px]' 
            : 'top-0'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              {/* Hamburger — mobile only */}
              <button
                onClick={(e) => { e.stopPropagation(); setSidebarOpen(true); }}
                className="lg:hidden w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-slate-700 hover:text-white transition-all cursor-pointer border border-slate-700"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>

              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25 hidden lg:flex">
                <i className="ri-shield-star-line text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-base font-bold text-white leading-none">
                  <span className="lg:hidden">{currentMenuItem.label}</span>
                  <span className="hidden lg:inline">Admin Dashboard</span>
                </h1>
                <p className="text-slate-400 text-xs mt-0.5">
                  <span className="lg:hidden">{currentMenuItem.desc}</span>
                  <span className="hidden lg:inline">Manage your business</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Current User Info */}
              {currentUser && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800 border border-slate-700">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                    {currentUser.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex flex-col">
                    <span className="text-white text-xs font-semibold leading-none">{currentUser.name}</span>
                    <span className="text-slate-400 text-[10px] leading-none mt-0.5">{currentUser.role_name || 'Admin'}</span>
                  </div>
                </div>
              )}
              <span className="text-slate-500 text-xs hidden sm:block font-medium">
                {lastRefresh.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </span>
              <button
                onClick={() => loadActiveOrders(false)}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-slate-300 text-sm font-medium hover:bg-slate-700 hover:text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 border border-slate-700"
              >
                <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <Link
                to="/"
                className="hidden lg:flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium hover:shadow-lg hover:shadow-purple-500/25 transition-all whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i>
                Store
              </Link>
              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-500/10 text-red-400 text-sm font-medium hover:bg-red-500/20 hover:text-red-300 transition-all cursor-pointer whitespace-nowrap border border-red-500/20"
              >
                <i className="ri-logout-box-line"></i>
                <span className="hidden sm:inline">Logout</span>
              </button>
              {/* Pending badge on mobile */}
              {counts.pending > 0 && (
                <div className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-lg shadow-amber-500/25">
                  <i className="ri-time-line"></i>
                  {counts.pending}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6 items-start">

        {/* ── DESKTOP SIDEBAR — Premium Dark Navigation ── */}
        <aside className="hidden lg:flex flex-col w-64 shrink-0 sticky top-24 gap-3">
          {/* Logo Card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 shadow-lg shadow-slate-900/20">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                <i className="ri-shield-star-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">Admin Panel</p>
                <p className="text-slate-400 text-xs">ClothyBazar</p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-3 shadow-lg shadow-slate-900/20">
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider px-3 pt-1 pb-2">Menu</p>
            <div className="space-y-1">
              {MENU_ITEMS.map(item => {
                const badge = getTabBadge(item.key);
                const isActive = mainTab === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => setMainTab(item.key)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap group ${
                      isActive 
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25' 
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                      isActive ? 'bg-white/20' : 'bg-slate-800 group-hover:bg-slate-700'
                    }`}>
                      <i className={`${item.icon} ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`}></i>
                    </div>
                    <span className="flex-1 text-left">{item.label}</span>
                    {badge !== null && badge > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        isActive ? 'bg-white/25 text-white' : 'bg-rose-500 text-white'
                      }`}>
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className="bg-slate-900 rounded-2xl border border-slate-800 p-4 shadow-lg shadow-slate-900/20">
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-3">Live Stats</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                <span className="text-slate-400 text-xs flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>Pending
                </span>
                <span className="text-amber-400 font-bold text-sm">{counts.pending}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-500/20">
                <span className="text-slate-400 text-xs flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span>Delivered
                </span>
                <span className="text-emerald-400 font-bold text-sm">{counts.delivered}</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-rose-500/10 to-red-500/10 border border-rose-500/20">
                <span className="text-slate-400 text-xs flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-400"></span>Cancelled
                </span>
                <span className="text-rose-400 font-bold text-sm">{counts.cancelled}</span>
              </div>
              <div className="border-t border-slate-800 pt-3 mt-2">
                <div className="flex items-center justify-between p-2.5 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
                  <span className="text-slate-400 text-xs flex items-center gap-2">
                    <i className="ri-money-rupee-circle-line text-emerald-400 text-sm"></i>Revenue
                  </span>
                  <span className="text-emerald-400 font-bold text-sm">₹{periodRevenue.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* AI Product Bot Button */}
          <button
            onClick={() => setShowAIBot(true)}
            className="w-full bg-gradient-to-br from-orange-500 to-pink-500 rounded-2xl p-4 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transition-all cursor-pointer text-left group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm group-hover:scale-105 transition-transform">
                <i className="ri-robot-2-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">AI Product Bot</p>
                <p className="text-white/80 text-xs">Chat karke product add karo</p>
              </div>
            </div>
          </button>

          {/* Pro Badge */}
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-4 shadow-lg shadow-purple-500/25">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <i className="ri-vip-crown-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white font-bold text-sm">Pro Dashboard</p>
                <p className="text-white/70 text-xs">All features active</p>
              </div>
            </div>
          </div>

        </aside>

        {/* ── MAIN CONTENT — Premium Glassmorphism Cards ── */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* ── ACTIVE ORDERS PANEL ── */}
          {mainTab === 'active' && (
          <>
          {/* ── DATE FILTER BAR — Premium Glassmorphism ── */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-white/50 shadow-lg shadow-slate-200/50 p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                <i className="ri-calendar-event-line text-white text-sm"></i>
              </div>
              <div className="flex-1">
                <p className="text-slate-800 font-bold text-sm">Date Filter</p>
                <p className="text-slate-400 text-xs">Select time period for analytics</p>
              </div>
              <span className="text-xs text-slate-400 flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-lg">
                <i className="ri-time-line"></i>
                {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {DATE_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => handleDateChange(tab.key)}
                  className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    datePeriod === tab.key
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/25'
                      : 'bg-slate-50 text-slate-500 hover:bg-slate-100 border border-slate-200'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  <span className="leading-none text-center">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ── STATS CARDS — Premium Gradient Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Orders Card */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl transition-all group">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-lg shadow-slate-500/25 group-hover:scale-105 transition-transform">
                  <i className="ri-shopping-bag-2-line text-white text-lg"></i>
                </div>
                <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-2 py-1 rounded-full">{periodLabel}</span>
              </div>
              <p className="text-3xl font-bold text-slate-800">{totalOrders}</p>
              <p className="text-slate-400 text-xs mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                Total Orders
              </p>
            </div>

            {/* Delivered Card */}
            <div className="bg-white rounded-2xl p-5 border border-emerald-100 shadow-lg shadow-emerald-200/50 hover:shadow-xl transition-all group">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/25 group-hover:scale-105 transition-transform">
                  <i className="ri-check-double-line text-white text-lg"></i>
                </div>
                <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">{periodLabel}</span>
              </div>
              <p className="text-3xl font-bold text-emerald-600">{counts.delivered}</p>
              <p className="text-emerald-500/70 text-xs mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                Delivered
              </p>
            </div>

            {/* Pending Card */}
            <div className="bg-white rounded-2xl p-5 border border-amber-100 shadow-lg shadow-amber-200/50 hover:shadow-xl transition-all group">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:scale-105 transition-transform">
                  <i className="ri-time-line text-white text-lg"></i>
                </div>
                <span className="text-[10px] text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-full">{periodLabel}</span>
              </div>
              <p className="text-3xl font-bold text-amber-600">{counts.pending}</p>
              <p className="text-amber-500/70 text-xs mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                Pending
              </p>
            </div>

            {/* Cancelled Card */}
            <div className="bg-white rounded-2xl p-5 border border-rose-100 shadow-lg shadow-rose-200/50 hover:shadow-xl transition-all group">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center shadow-lg shadow-rose-500/25 group-hover:scale-105 transition-transform">
                  <i className="ri-close-circle-line text-white text-lg"></i>
                </div>
                <span className="text-[10px] text-rose-600 font-medium bg-rose-50 px-2 py-1 rounded-full">{periodLabel}</span>
              </div>
              <p className="text-3xl font-bold text-rose-600">{counts.cancelled}</p>
              <p className="text-rose-500/70 text-xs mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                Cancelled
              </p>
            </div>
          </div>

          {/* Order Mode Toggle */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center">
                  <i className="ri-settings-3-line text-gray-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Order Accept Mode</p>
                  <p className="text-gray-400 text-xs">How new orders are handled</p>
                </div>
                <button
                  onClick={() => setShowModeInfo(p => !p)}
                  className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-all ml-1"
                >
                  <i className="ri-question-line text-gray-500 text-xs"></i>
                </button>
              </div>
              <div className="flex items-center bg-gray-100 rounded-2xl p-1 gap-1">
                <button
                  onClick={() => { setOrderMode('auto'); showToast('Auto Accept mode ON!', 'info'); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    orderMode === 'auto' ? 'bg-green-500 text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-flashlight-line"></i>Auto Accept
                </button>
                <button
                  onClick={() => { setOrderMode('manual'); showToast('Manual mode ON!', 'info'); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    orderMode === 'manual' ? 'bg-amber-500 text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className="ri-hand-coin-line"></i>Manual
                </button>
              </div>
            </div>

            {showModeInfo && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                  <p className="text-green-700 font-bold text-xs flex items-center gap-1 mb-1">
                    <i className="ri-flashlight-line"></i> Auto Accept
                  </p>
                  <p className="text-green-600 text-xs leading-relaxed">New orders are automatically confirmed. No alert or ring — just a quick beep.</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                  <p className="text-amber-700 font-bold text-xs flex items-center gap-1 mb-1">
                    <i className="ri-hand-coin-line"></i> Manual Accept
                  </p>
                  <p className="text-amber-600 text-xs leading-relaxed">Device rings &amp; vibrates for every new order. You must Accept or Reject.</p>
                </div>
              </div>
            )}

            <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold ${
              orderMode === 'auto' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-amber-50 text-amber-700 border border-amber-100'
            }`}>
              <span className={`w-2 h-2 rounded-full animate-pulse ${orderMode === 'auto' ? 'bg-green-500' : 'bg-amber-500'}`}></span>
              {orderMode === 'auto'
                ? 'Auto Accept is active — new orders will be confirmed automatically'
                : `Manual mode active — device will ring & vibrate for new orders${pendingAlerts.length > 0 ? ` · ${pendingAlerts.length} waiting!` : ''}`
              }
            </div>

            {permission === 'default' && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl p-3">
                <div className="flex items-start gap-2 mb-2.5">
                  <i className="ri-notification-3-line text-orange-500 shrink-0"></i>
                  <div className="min-w-0">
                    <p className="text-orange-800 text-xs font-bold leading-tight">Screen Lock Alert Enable Karo</p>
                    <p className="text-orange-600 text-xs mt-0.5 leading-relaxed">
                      Phone ki screen band hone ke baad bhi order aane par <strong>ring bajegi + notification aayegi</strong> jab tak accept/reject na karo
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    const granted = await requestPermission();
                    showToast(
                      granted
                        ? 'Screen lock alerts ON! Ab phone band hone pe bhi ring bajegi!'
                        : 'Permission nahi mili.',
                      granted ? 'success' : 'error'
                    );
                  }}
                  className="w-full py-2 rounded-xl bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-lock-unlock-line"></i>
                  Screen Lock Alert Enable Karo
                </button>
              </div>
            )}
            {permission === 'granted' && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-green-50 border border-green-100 text-xs text-green-700 font-semibold">
                  <i className="ri-lock-unlock-line text-green-500"></i>
                  Screen lock alerts ON — order aane pe alarm bajega!
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                  <p className="text-blue-700 text-xs">
                    <i className="ri-information-line text-blue-500 mr-1"></i>
                    <strong>Order aane pe:</strong> Notification + ring shuru → Accept/Reject karo → Alarm band!
                  </p>
                </div>
              </div>
            )}
            {permission === 'denied' && (
              <div className="mt-3 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                <p className="text-red-600 text-xs font-medium flex items-start gap-1.5">
                  <i className="ri-notification-off-line text-red-400 shrink-0 mt-0.5"></i>
                  <span>Notifications blocked hain. Phone settings mein jaao → Browser → Notifications ON karo, tab screen lock pe ring bajegi.</span>
                </p>
              </div>
            )}
          </div>

          {/* ── PAUSE ORDERS CARD ── */}
          <div className={`rounded-2xl border p-4 mb-5 transition-all ${pauseStatus.paused ? 'bg-red-50 border-red-300' : 'bg-white border-gray-100'}`}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${pauseStatus.paused ? 'bg-red-500' : 'bg-gray-100'}`}>
                  <i className={`${pauseStatus.paused ? 'ri-pause-circle-fill' : 'ri-play-circle-line'} text-gray-600 text-sm`}></i>
                </div>
                <div>
                  <p className="text-gray-900 font-bold text-sm">Order Accepting</p>
                  <p className="text-gray-400 text-xs">Customer ke cart se order place hoga ya nahi</p>
                </div>
              </div>
              <div className="flex items-center bg-gray-100 rounded-2xl p-1 gap-1">
                <button
                  onClick={() => !pauseStatus.paused && undefined}
                  disabled={!pauseStatus.paused}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    !pauseStatus.paused ? 'bg-green-500 text-white' : 'text-gray-500 hover:text-gray-700 disabled:cursor-default'
                  }`}
                >
                  <i className="ri-checkbox-circle-line"></i>Accepting
                </button>
                <button
                  onClick={() => pauseStatus.paused && undefined}
                  disabled={pauseStatus.paused}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    pauseStatus.paused ? 'bg-red-500 text-white' : 'text-gray-500 hover:text-gray-700 disabled:cursor-default'
                  }`}
                >
                  <i className="ri-pause-circle-line"></i>Paused
                </button>
              </div>
            </div>

            {/* Reason Selection — show always */}
            <div className="mb-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">
                {pauseStatus.paused ? 'Current Reason:' : 'Select Reason to Pause:'}
              </p>
              <div className="space-y-2">
                {PAUSE_REASONS.map(reason => (
                  <div key={reason.id}>
                    <button
                      onClick={() => !pauseStatus.paused && setSelectedPauseReason(reason.id)}
                      disabled={pauseStatus.paused}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition-all text-left cursor-pointer ${
                        pauseStatus.paused
                          ? (pauseStatus.reason === reason.id
                              ? 'bg-red-100 border-2 border-red-400 text-red-700 cursor-default'
                              : 'bg-gray-50 border border-gray-100 text-gray-300 cursor-default')
                          : (selectedPauseReason === reason.id
                              ? 'bg-gray-900 text-white border-2 border-gray-900'
                              : 'bg-gray-50 border border-gray-200 text-gray-600 hover:border-gray-400')
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                        pauseStatus.paused
                          ? (pauseStatus.reason === reason.id ? 'bg-red-200' : 'bg-gray-100')
                          : (selectedPauseReason === reason.id ? 'bg-white/20' : 'bg-white')
                      }`}>
                        <i className={`${reason.icon} text-sm`}></i>
                      </div>
                      <span className="leading-tight">
                        {reason.id === 'custom'
                          ? (pauseStatus.paused && pauseStatus.reason === 'custom' && pauseStatus.customReason
                              ? pauseStatus.customReason
                              : 'Custom Reason (apna likho)')
                          : reason.label}
                      </span>
                      {(pauseStatus.paused ? pauseStatus.reason : selectedPauseReason) === reason.id && (
                        <i className="ri-check-line ml-auto shrink-0"></i>
                      )}
                    </button>

                    {/* Custom reason text input — only show when custom is selected & not yet paused */}
                    {reason.id === 'custom' && selectedPauseReason === 'custom' && !pauseStatus.paused && (
                      <div className="mt-2 ml-1">
                        <div className="relative">
                          <i className="ri-edit-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                          <input
                            type="text"
                            value={customPauseReason}
                            onChange={e => setCustomPauseReason(e.target.value)}
                            placeholder="Apna reason yahan type karo..."
                            maxLength={100}
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border-2 border-gray-900 bg-white text-xs text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-gray-300 outline-none"
                          />
                        </div>
                        <p className="text-gray-400 text-[10px] mt-1 text-right">{customPauseReason.length}/100</p>
                      </div>
                    )}

                    {/* Show saved custom reason when paused */}
                    {reason.id === 'custom' && pauseStatus.paused && pauseStatus.reason === 'custom' && pauseStatus.customReason && (
                      <div className="mt-2 ml-1 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-red-600 text-xs font-medium flex items-center gap-1.5">
                          <i className="ri-message-2-line text-red-400"></i>
                          &ldquo;{pauseStatus.customReason}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Button */}
            {pauseStatus.paused ? (
              <button
                onClick={() => handleTogglePause(false)}
                className="w-full py-3 rounded-xl bg-green-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-base"></i>
                Resume — Start Accepting Orders
              </button>
            ) : (
              <button
                onClick={() => handleTogglePause(true)}
                className="w-full py-3 rounded-xl bg-red-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-pause-circle-line text-base"></i>
                Pause Orders with This Reason
              </button>
            )}

            {pauseStatus.paused && pauseStatus.pausedAt && (
              <p className="text-center text-red-500 text-xs mt-2 flex items-center justify-center gap-1">
                <i className="ri-time-line"></i>
                Paused since {new Date(pauseStatus.pausedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>

          </> /* end active orders wrapper */
          )}

          {mainTab === 'active' && (
            <>
              <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search by name, phone, order ID..."
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-200 outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 shrink-0">
                    <i className="ri-filter-3-line"></i>
                    <span className="font-medium">{filteredActive.length} orders</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {ACTIVE_TABS.map(tab => {
                    const cnt = tab.key === 'all'
                      ? activeOrders.length
                      : activeOrders.filter(o => o.status === tab.key).length;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setActiveFilter(tab.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                          activeFilter === tab.key
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <i className={tab.icon}></i>
                        {tab.label}
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeFilter === tab.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                          {cnt}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
                  <p className="text-gray-500 text-sm">Loading orders...</p>
                </div>
              ) : filteredActive.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
                  <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-3xl text-green-400"></i>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-700 font-semibold">All clear!</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {search
                        ? 'No active orders match your search'
                        : `No active orders for ${periodLabel.toLowerCase()}. New orders will appear here.`
                      }
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredActive.map(order => (
                    <OrderCard key={order.id} order={order} onStatusChange={handleStatusChange} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── PRODUCTS PANEL ── */}
          {mainTab === 'products' && <ProductsPanel />}

          {/* ── ANALYTICS PANEL ── */}
          {mainTab === 'analytics' && <AnalyticsPanel />}

          {/* ── DELIVERY BOYS PANEL ── */}
          {mainTab === 'delivery' && <DeliveryBoysPanel />}

          {/* ── DELIVERY STATS PANEL ── */}
          {mainTab === 'deliverystats' && <DeliveryStatsPanel />}

          {/* ── LIVE DELIVERY MAP PANEL ── */}
          {mainTab === 'livemap' && <LiveDeliveryMap />}

          {/* ── OUT OF AREA VISITORS PANEL ── */}
          {mainTab === 'visitors' && <OutOfAreaPanel />}

          {/* ── CUSTOMERS PANEL ── */}
          {mainTab === 'customers' && <CustomerRecordsPanel />}

          {/* ── GIFT CODES PANEL ── */}
          {mainTab === 'giftcodes' && <GiftCodesPanel />}

          {/* ── COMPLAINTS PANEL ── */}
          {mainTab === 'complaints' && <ComplaintsPanel />}

          {/* ── VISITOR ACTIVITY PANEL ── */}
          {mainTab === 'activity' && <VisitorActivityPanel />}

          {/* ── VIDEO POPUP PANEL ── */}
          {mainTab === 'videopop' && <VideoPopupPanel onSave={() => showToast('Video Popup settings saved!', 'success')} />}

          {/* ── ROLE MANAGEMENT PANEL ── */}
          {mainTab === 'roles' && <RoleManagementPanel />}

          {/* ── TODAY'S DEALS PANEL ── */}
          {mainTab === 'deals' && <TodaysDealsPanel onToast={showToast} />}

          {/* ── STOCK ALERTS PANEL ── */}
          {mainTab === 'stockalerts' && <StockAlertsPanel onToast={showToast} />}

          {/* ── STOCK HISTORY PANEL ── */}
          {mainTab === 'stockhistory' && <StockHistoryPanel />}

          {/* ── HERO BANNER PANEL ── */}
          {mainTab === 'herobanner' && <HeroBannerPanel onToast={showToast} />}

          {/* ── BANNER LAYOUT PANEL ── */}
          {mainTab === 'bannerlayout' && <BannerLayoutPanel onToast={showToast} />}

          {/* ── COLLECTION LAYOUT PANEL ── */}
          {mainTab === 'collectionlayout' && <CollectionLayoutPanel onToast={showToast} />}

          {/* ── HOME PAGE LAYOUT PANEL ── */}
          {mainTab === 'homelayout' && <HomeLayoutPanel onToast={showToast} />}

          {/* ── THEME PANEL ── */}
          {mainTab === 'theme' && <ThemePanel onToast={showToast} />}

          {/* ── META PIXEL PANEL ── */}
          {mainTab === 'metapixel' && <MetaPixelPanel onToast={showToast} />}

          {/* ── SETTINGS PANEL ── */}
          {mainTab === 'settings' && (
            <div className="space-y-5">

              {/* ── SERVICE LOCATION CARD ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <i className="ri-map-pin-2-fill text-orange-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Delivery Service Location</p>
                    <p className="text-gray-400 text-xs">Apna panel location set karo — 10km radius mein hi delivery hogi</p>
                  </div>
                </div>

                {/* Current saved location */}
                {adminLoc && (
                  <div className="mb-4 bg-green-50 border border-green-200 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-green-500 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-white text-base"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-green-800 font-bold text-sm">Location Set Hai</p>
                        <p className="text-green-700 text-xs leading-relaxed mt-0.5 break-words">{adminLoc.address}</p>
                        <div className="flex items-center gap-3 mt-2 flex-wrap">
                          <span className="text-green-600 text-xs font-mono bg-green-100 px-2 py-1 rounded-lg">
                            {adminLoc.lat.toFixed(5)}, {adminLoc.lng.toFixed(5)}
                          </span>
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {new Date(adminLoc.setAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Mini map preview */}
                    <div className="mt-3 rounded-xl overflow-hidden border border-green-200" style={{ height: '160px' }}>
                      <iframe
                        title="Service Area Map"
                        width="100%"
                        height="160"
                        style={{ border: 0 }}
                        loading="lazy"
                        src={`https://maps.google.com/maps?q=${adminLoc.lat},${adminLoc.lng}&z=13&output=embed`}
                      />
                    </div>
                    <div className="mt-3 flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
                      <div className="w-7 h-7 flex items-center justify-center shrink-0">
                        <i className="ri-radar-line text-orange-500 text-base"></i>
                      </div>
                      <p className="text-orange-700 text-xs font-semibold">
                        Delivery zone: Is location se <strong>10km radius</strong> ke andar ke customers hi order kar sakte hain
                      </p>
                    </div>
                  </div>
                )}

                {/* Set new location */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleDetectAdminLocation}
                      disabled={locDetecting}
                      className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm hover:shadow-lg transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
                    >
                      {locDetecting ? (
                        <><i className="ri-loader-4-line animate-spin text-lg"></i>Detecting Location...</>
                      ) : (
                        <><i className="ri-crosshair-2-line text-lg"></i>{adminLoc ? 'Update My Location' : 'Detect My Current Location'}</>
                      )}
                    </button>
                  </div>

                  {locError && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-500 mt-0.5 shrink-0"></i>
                      <p className="text-red-600 text-xs">{locError}</p>
                    </div>
                  )}

                  {locMapCoords && !locDetecting && (
                    <>
                      <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: '200px' }}>
                        <iframe
                          title="New Location Map"
                          width="100%"
                          height="200"
                          style={{ border: 0 }}
                          loading="lazy"
                          src={`https://maps.google.com/maps?q=${locMapCoords.lat},${locMapCoords.lng}&z=14&output=embed`}
                        />
                      </div>

                      {locAddress && (
                        <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <p className="text-gray-500 text-xs mb-1 font-semibold">Detected Address:</p>
                          <p className="text-gray-700 text-xs leading-relaxed">{locAddress}</p>
                          <p className="text-gray-400 text-xs mt-1 font-mono">{locMapCoords.lat.toFixed(5)}, {locMapCoords.lng.toFixed(5)}</p>
                        </div>
                      )}

                      {/* Show 10km range info */}
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                        <div className="w-7 h-7 flex items-center justify-center shrink-0">
                          <i className="ri-information-line text-amber-500"></i>
                        </div>
                        <div>
                          <p className="text-amber-700 text-xs font-bold">Yahan se 10km radius:</p>
                          <p className="text-amber-600 text-xs mt-1">
                            Is location se 10km ke andar ke sare customers order kar sakenge. Baaki ko &ldquo;Delivery not available&rdquo; dikhega.
                          </p>
                          <p className="text-amber-500 text-xs mt-1 font-mono">
                            Lat: {locMapCoords.lat.toFixed(4)} · Lng: {locMapCoords.lng.toFixed(4)}
                          </p>
                          {adminLoc && (
                            <p className="text-amber-600 text-xs mt-1 font-semibold">
                              Old location se distance: {calcDistanceKm(adminLoc.lat, adminLoc.lng, locMapCoords.lat, locMapCoords.lng).toFixed(1)}km
                            </p>
                          )}
                        </div>
                      </div>

                      {locConfirmed ? (
                        <div className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-green-100 border border-green-200 text-green-700 font-bold text-sm">
                          <i className="ri-checkbox-circle-fill text-green-500"></i>
                          Location Saved Successfully!
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSaveAdminLocation}
                          className="w-full py-4 rounded-2xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-save-line text-lg"></i>
                          Save This Location as Service Center
                        </button>
                      )}
                    </>
                  )}
                </div>

                {!adminLoc && !locMapCoords && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        <i className="ri-alert-line text-amber-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-amber-800 font-bold text-sm">Location Set Nahi Hai!</p>
                        <p className="text-amber-700 text-xs leading-relaxed mt-0.5">
                          Abhi default Dehradun center use ho raha hai. Apna exact panel location set karo taaki sahi customers tak delivery ho.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* How it works info */}
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-amber-500"></i>
                  Yeh Feature Kaise Kaam Karta Hai
                </p>
                <ul className="space-y-3 text-gray-500 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <span>Upar &ldquo;Detect My Current Location&rdquo; button dabao — browser aapka location detect karega</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <span>&ldquo;Save This Location&rdquo; karo — yeh aapke panel ki service center ban jaayegi</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">3</span>
                    <span>10km ke andar ke customers ko shop dikhega aur order kar sakte hain</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-red-100 text-red-500 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">4</span>
                    <span>10km se bahar walon ko dikhega: &ldquo;Delivery not available at your location — We are reaching you as soon as possible&rdquo;</span>
                  </li>
                </ul>
              </div>

              {/* QR Code section */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                    <i className="ri-qr-code-line text-gray-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Payment QR Code</p>
                    <p className="text-gray-400 text-xs">Delivery boys ke liye online payment QR</p>
                  </div>
                </div>

                {qrImage ? (
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-2xl p-6 flex flex-col items-center">
                      <div className="w-56 h-56 bg-white rounded-xl border-2 border-gray-200 overflow-hidden flex items-center justify-center mb-4">
                        <img src={qrImage} alt="Company Payment QR" className="w-full h-full object-contain" />
                      </div>
                      <p className="text-gray-500 text-sm text-center">
                        <i className="ri-information-line mr-1"></i>
                        Yeh QR delivery panel mein customers ko dikhega
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={qrUploading}
                        className="flex-1 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {qrUploading ? <><i className="ri-loader-4-line animate-spin"></i>Upload ho raha hai...</> : <><i className="ri-refresh-line"></i>QR Change Karo</>}
                      </button>
                      <button
                        onClick={handleRemoveQr}
                        className="px-4 py-3 rounded-xl bg-red-50 text-red-600 font-bold text-sm flex items-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-delete-bin-line"></i>Hatao
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-upload-cloud-2-line text-3xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-700 font-semibold mb-1">QR Code Upload Karo</p>
                    <p className="text-gray-400 text-sm mb-5">PNG, JPG, JPEG · Max 2MB</p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={qrUploading}
                      className="px-6 py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {qrUploading ? <><i className="ri-loader-4-line animate-spin"></i>Upload ho raha hai...</> : <><i className="ri-image-add-line"></i>QR Select Karo</>}
                    </button>
                  </div>
                )}

                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleQrUpload} className="hidden" />
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <p className="text-gray-700 font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-amber-500"></i>
                  QR Code Setup Instructions
                </p>
                <ul className="space-y-2 text-gray-500 text-sm">
                  <li className="flex items-start gap-2"><span className="text-gray-400">1.</span><span>Apne UPI app (PhonePe, GPay, Paytm) se QR code download karo</span></li>
                  <li className="flex items-start gap-2"><span className="text-gray-400">2.</span><span>Yahan upload kar do — delivery boys ke panel mein automatically dikhega</span></li>
                  <li className="flex items-start gap-2"><span className="text-gray-400">3.</span><span>Customer online payment karega toh seedha aapke account mein aayega</span></li>
                </ul>
              </div>

              {/* ── PWA INSTALL CARD ── */}
              <div className={`rounded-2xl border p-5 ${pwaInstalled ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pwaInstalled ? 'bg-green-500' : 'bg-blue-500'}`}>
                    <i className={`${pwaInstalled ? 'ri-checkbox-circle-fill' : 'ri-download-cloud-line'} text-white text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">{pwaInstalled ? 'App Added to Home Screen!' : 'Add to Home Screen (App Jaisa)'}</p>
                    <p className={`text-xs ${pwaInstalled ? 'text-green-600' : 'text-blue-600'}`}>
                      {pwaInstalled 
                        ? 'Home screen icon se open karo — screen lock pe bhi alarm bajega!' 
                        : 'Chrome menu se "Add to Home Screen" karo'}
                    </p>
                  </div>
                </div>

                {!pwaInstalled && (
                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-3 border border-blue-100">
                      <p className="text-gray-700 text-xs font-semibold mb-2">Kaise Add Karein:</p>
                      <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">1.</span>
                          <span><strong>Chrome browser</strong> mein yeh admin panel open karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">2.</span>
                          <span>Top right <strong>3-dots menu</strong> (⋮) dabao</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">3.</span>
                          <span><strong>"Add to Home Screen"</strong> select karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">4.</span>
                          <span><strong>"Add"</strong> button pe tap karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">5.</span>
                          <span>Home screen pe <strong>"CB Admin"</strong> icon milega!</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-green-50 rounded-xl p-3 border border-green-200">
                      <p className="text-green-800 text-xs font-semibold mb-1 flex items-center gap-1">
                        <i className="ri-checkbox-circle-line text-green-500"></i>
                        <span>
                          <strong>Add to Home Screen</strong> = APK Jaisa!
                        </span>
                      </p>
                      <p className="text-green-700 text-xs leading-relaxed">
                        Yeh bhi <strong>app jaisa hi kaam karega</strong> — full screen, notifications, aur screen lock pe alarm sab kaam karega!
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-amber-800 text-xs font-semibold mb-1 flex items-center gap-1">
                        <i className="ri-lightbulb-flash-line text-amber-500"></i>
                        Kyun Add Karein?
                      </p>
                      <ul className="text-xs text-amber-700 space-y-1">
                        <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Screen lock hone ke baad bhi order alarm bajega</li>
                        <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Full screen app experience — no browser address bar</li>
                        <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>One tap se admin panel open — fast access</li>
                        <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i>Notifications aur sound properly kaam karega</li>
                      </ul>
                    </div>

                    {pwaPrompt.show && (
                      <button
                        onClick={async () => {
                          if (pwaPrompt.deferredPrompt) {
                            // @ts-expect-error - beforeinstallprompt event
                            await pwaPrompt.deferredPrompt.prompt();
                            // @ts-expect-error - beforeinstallprompt event
                            const { outcome } = await pwaPrompt.deferredPrompt.userChoice;
                            if (outcome === 'accepted') {
                              setPwaPrompt({ show: false, deferredPrompt: null });
                              setPwaInstalled(true);
                              showToast('App installed! Home screen se open karo.', 'success');
                            }
                          }
                        }}
                        className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-600 transition-all cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-line text-lg"></i>
                        Install App Now
                      </button>
                    )}
                  </div>
                )}

                {pwaInstalled && (
                  <div className="bg-green-100 rounded-xl p-3 border border-green-200 space-y-2">
                    <p className="text-green-700 text-xs font-semibold flex items-start gap-1.5">
                      <i className="ri-checkbox-circle-fill text-green-500 shrink-0 mt-0.5"></i>
                      <span>
                        <strong>Home screen pe add ho gaya!</strong> Ab "CB Admin" icon se open karo.
                      </span>
                    </p>
                    <div className="bg-white rounded-lg p-2 border border-green-200">
                      <p className="text-green-800 text-xs font-semibold mb-1">Screen Lock Alarm Setup:</p>
                      <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside">
                        <li>Home screen se <strong>"CB Admin"</strong> icon se open karo</li>
                        <li>Upar <strong>"Enable Sound"</strong> banner pe tap karo</li>
                        <li><strong>"Screen Lock Alert Enable Karo"</strong> button dabao</li>
                        <li>Phone lock karo — ab bhi har 8s mein notification aayegi!</li>
                      </ol>
                    </div>
                  </div>
                )}
              </div>

              {/* ── WHATSAPP BACKUP NOTIFICATIONS ── */}
              <div className="bg-green-50 rounded-2xl border border-green-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                    <i className="ri-whatsapp-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">WhatsApp Backup Notifications</p>
                    <p className="text-green-600 text-xs">Most reliable — works even when browser is closed!</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-3 border border-green-100">
                    <p className="text-gray-700 text-xs font-semibold mb-2">Kaise Setup Karein:</p>
                    <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                      <li className="flex items-start gap-2">
                        <span className="shrink-0">1.</span>
                        <span>Upar <strong>"WhatsApp Order Notifications"</strong> section mein apna number daalo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="shrink-0">2.</span>
                        <span><strong>Supabase Edge Function</strong> setup karo (WhatsApp API token required)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="shrink-0">3.</span>
                        <span>Order aane pe <strong>WhatsApp message</strong> aayega — 100% reliable!</span>
                      </li>
                    </ol>
                  </div>

                  <div className="bg-green-100 rounded-xl p-3 border border-green-200">
                    <p className="text-green-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line text-green-600"></i>
                      Kyun Best Hai?
                    </p>
                    <p className="text-green-700 text-xs leading-relaxed">
                      WhatsApp notifications <strong>browser band hone pe bhi aate hain</strong>! 
                      Phone ki screen lock ho, Chrome band ho — tab bhi WhatsApp message aayega.
                      <strong>Recommended: Dono setup karo</strong> — Browser + WhatsApp!
                    </p>
                  </div>
                </div>
              </div>

              {/* ── BATTERY OPTIMIZATION GUIDE ── */}
              {!pwaInstalled && (
                <div className="bg-amber-50 rounded-2xl border border-amber-200 p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
                      <i className="ri-battery-charge-line text-white text-lg"></i>
                    </div>
                    <div>
                      <p className="text-gray-900 font-bold text-base">Battery Settings (Important!)</p>
                      <p className="text-amber-600 text-xs">Screen lock pe alarm ke liye yeh zaroori hai</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-white rounded-xl p-3 border border-amber-100">
                      <p className="text-gray-700 text-xs font-semibold mb-2">Android Phone Settings:</p>
                      <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">1.</span>
                          <span>Phone <strong>Settings</strong> app open karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">2.</span>
                          <span><strong>Apps</strong> → <strong>Chrome</strong> (ya jo browser use kar rahe ho)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">3.</span>
                          <span><strong>Battery</strong> → <strong>Unrestricted</strong> select karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">4.</span>
                          <span><strong>Background activity</strong> → <strong>ON</strong> karo</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="shrink-0">5.</span>
                          <span><strong>Notifications</strong> → <strong>Allow all</strong> karo</span>
                        </li>
                      </ol>
                    </div>

                    <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                      <p className="text-red-700 text-xs font-semibold mb-1 flex items-center gap-1">
                        <i className="ri-error-warning-line text-red-500"></i>
                        Kyun Zaroori Hai?
                      </p>
                      <p className="text-red-600 text-xs leading-relaxed">
                        Android battery saver <strong>Chrome ko background mein band kar deta hai</strong>. 
                        Isse screen lock pe notifications nahi aate. 
                        <strong>"Unrestricted"</strong> mode mein alarm properly kaam karega!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── SCREEN LOCK ALARM TEST ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <i className="ri-alarm-warning-line text-amber-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Screen Lock Alarm Test</p>
                    <p className="text-gray-400 text-xs">Phone lock hone ke baad bhi order ka alarm bajega</p>
                  </div>
                </div>

                {/* Important Limitation Warning */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                  <p className="text-red-800 text-xs font-semibold mb-1 flex items-center gap-1">
                    <i className="ri-error-warning-line text-red-500"></i>
                    Important: Browser Kill Hone Pe Notification Nahi Aayega!
                  </p>
                  <p className="text-red-700 text-xs leading-relaxed">
                    Agar aap <strong>Chrome ko completely close kar dete ho</strong> (swipe up karke), tab notification <strong>nahi aayega</strong>. Iske liye:
                  </p>
                  <ul className="text-xs text-red-700 space-y-1 mt-2">
                    <li className="flex items-start gap-1"><i className="ri-check-line text-red-500 shrink-0"></i><strong>Home Screen se PWA open karo</strong> — Add to Home Screen karo</li>
                    <li className="flex items-start gap-1"><i className="ri-check-line text-red-500 shrink-0"></i><strong>Chrome ko background mein chhodo</strong> — minimize karo, band mat karo</li>
                    <li className="flex items-start gap-1"><i className="ri-check-line text-red-500 shrink-0"></i><strong>Battery settings mein Chrome ko Unrestricted karo</strong></li>
                  </ul>
                </div>

                {/* Important Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4">
                  <p className="text-blue-800 text-xs font-semibold mb-1 flex items-center gap-1">
                    <i className="ri-information-line text-blue-500"></i>
                    Alarm kab bajega?
                  </p>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>Jab naya order aayega</strong> — tab alarm shuru hoga</li>
                    <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>Har 8 second mein reminder</strong> — jab tak accept/reject na karo</li>
                    <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>Accept/Reject kar diya</strong> — alarm band ho jayega!</li>
                    <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>Koi order nahi hai</strong> — kuch nahi bajega</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  {/* System Check */}
                  <button
                    onClick={async () => {
                      const checks = [];

                      // Check 1: Notification API
                      if (!('Notification' in window)) {
                        checks.push('❌ Notification API not supported');
                      } else {
                        checks.push(`✅ Notification API available (permission: ${Notification.permission})`);
                      }

                      // Check 2: Service Worker
                      if (!('serviceWorker' in navigator)) {
                        checks.push('❌ Service Worker not supported');
                      } else {
                        try {
                          const reg = await navigator.serviceWorker.ready;
                          checks.push(`✅ Service Worker ready (active: ${reg.active ? 'YES' : 'NO'})`);
                          if (reg.active) {
                            checks.push(`   SW State: ${reg.active.state}`);
                          }
                        } catch (e) {
                          checks.push('❌ Service Worker not ready: ' + (e instanceof Error ? e.message : 'Unknown'));
                        }
                      }

                      // Check 3: Vibration
                      if ('vibrate' in navigator) {
                        checks.push('✅ Vibration API available');
                        navigator.vibrate(50); // Test vibration
                      } else {
                        checks.push('❌ Vibration API not available');
                      }

                      // Check 4: Audio Context
                      try {
                        const AudioContext = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
                        if (AudioContext) {
                          const ctx = new AudioContext();
                          checks.push(`✅ AudioContext available (state: ${ctx.state})`);
                        }
                      } catch {
                        checks.push('❌ AudioContext not available');
                      }

                      // Check 5: PWA Mode
                      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as unknown as { standalone?: boolean }).standalone === true;
                      checks.push(isStandalone ? '✅ Running in PWA/Standalone mode' : '⚠️ Running in browser mode (Add to Home Screen recommended)');

                      alert('System Check:\n\n' + checks.join('\n'));
                    }}
                    className="w-full py-3 rounded-xl bg-purple-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-purple-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-tools-line text-lg"></i>
                    System Check (Sab OK hai?)
                  </button>

                  {/* Simple Direct Notification Test */}
                  <button
                    onClick={async () => {
                      if (permission !== 'granted') {
                        const granted = await requestPermission();
                        if (!granted) {
                          showToast('Notification permission chahiye!', 'error');
                          return;
                        }
                      }

                      // Direct notification test - no SW needed
                      try {
                        const notif = new Notification('🔔 Test: Order Aaya!', {
                          body: 'Yeh test hai - asli order aane pe aise hi notification aayega!',
                          icon: '/vite.svg',
                          requireInteraction: true,
                        });
                        console.log('Direct notification sent:', notif);
                        showToast('Test notification gayi! Ab phone lock karke dekho.', 'success');
                      } catch (err) {
                        console.error('Direct notification error:', err);
                        showToast('Direct notification failed: ' + (err instanceof Error ? err.message : 'Unknown'), 'error');
                      }
                    }}
                    className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-notification-3-line text-lg"></i>
                    Step 1: Test Order Notification
                  </button>

                  <button
                    onClick={async () => {
                      if (permission !== 'granted') {
                        showToast('Pehle "Test Order Notification" se permission lo!', 'error');
                        return;
                      }

                      if (!('serviceWorker' in navigator)) {
                        showToast('Service Worker support nahi hai!', 'error');
                        return;
                      }

                      try {
                        const registration = await navigator.serviceWorker.ready;
                        console.log('SW Registration:', registration);
                        console.log('SW Active:', registration.active);

                        if (!registration.active) {
                          showToast('SW active nahi hai. Page refresh karo.', 'error');
                          return;
                        }

                        // Simple test order - yeh asli order jaisa behave karega
                        const testOrder = {
                          id: 'TEST-' + Date.now(),
                          customer_name: 'Test Customer',
                          customer_phone: '+91 98765 43210',
                          customer_city: 'Test City',
                          final_amount: 999,
                          items: [{ id: 1, name: 'Test Product', price: 999, quantity: 1 }],
                          status: 'pending',
                          created_at: new Date().toISOString(),
                        };

                        console.log('Sending to SW:', testOrder);

                        // Send message to SW - yeh alarm shuru karega
                        registration.active.postMessage({
                          type: 'NEW_ORDER',
                          order: testOrder,
                        });

                        showToast('Test order alarm shuru! Phone lock karke dekho.', 'success');

                        // Also try direct notification from SW
                        setTimeout(async () => {
                          try {
                            await registration.showNotification('🔔 Test Order Alarm', {
                              body: 'Yeh test alarm hai - asli order aane pe aise hi bajega!',
                              icon: '/vite.svg',
                              requireInteraction: true,
                            });
                            console.log('SW direct notification sent');
                          } catch (e) {
                            console.error('SW direct notification error:', e);
                          }
                        }, 1000);

                      } catch (err) {
                        console.error('SW test error:', err);
                        showToast('Error: ' + (err instanceof Error ? err.message : 'Unknown'), 'error');
                      }
                    }}
                    className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-alarm-warning-line text-lg"></i>
                    Step 2: Test Screen Lock Alarm
                  </button>

                  <button
                    onClick={() => {
                      notifySWOrderActionTaken('TEST-ORDER-123');
                      showToast('Test alarm band kar diya!', 'info');
                    }}
                    className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-200 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-stop-circle-line text-lg"></i>
                    Stop Test Alarm
                  </button>

                  {/* Debug Info */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                    <p className="text-gray-700 text-xs font-semibold mb-2">Status:</p>
                    <div className="space-y-1 text-xs text-gray-500">
                      <p>Permission: <span className={permission === 'granted' ? 'text-green-600 font-bold' : 'text-amber-600'}>{permission}</span></p>
                      <p>SW Ready: <span className={swReady ? 'text-green-600 font-bold' : 'text-amber-600'}>{swReady ? 'Yes' : 'No'}</span></p>
                      <p>PWA Installed: <span className={pwaInstalled ? 'text-green-600 font-bold' : 'text-amber-600'}>{pwaInstalled ? 'Yes' : 'No'}</span></p>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <p className="text-green-700 text-xs font-semibold flex items-start gap-1.5">
                      <i className="ri-checkbox-circle-line text-green-500 shrink-0 mt-0.5"></i>
                      <span>
                        <strong>Kaise test karein:</strong><br/>
                        1. "System Check" dabao — sab OK hona chahiye<br/>
                        2. "Step 1" dabao — notification permission check<br/>
                        3. "Step 2" dabao — fake order alarm test<br/>
                        4. Phone lock karo — har 6s mein notification aayega
                      </span>
                    </p>
                  </div>

                  {/* Clear Cache Button */}
                  <button
                    onClick={async () => {
                      try {
                        // Unregister all service workers
                        if ('serviceWorker' in navigator) {
                          const regs = await navigator.serviceWorker.getRegistrations();
                          for (const reg of regs) {
                            await reg.unregister();
                            console.log('SW unregistered');
                          }
                        }
                        // Clear cache
                        if ('caches' in window) {
                          const keys = await caches.keys();
                          for (const key of keys) {
                            await caches.delete(key);
                            console.log('Cache deleted:', key);
                          }
                        }
                        // Clear localStorage items related to PWA
                        localStorage.removeItem('clothybazar_audio_unlocked');
                        showToast('Cache cleared! Page reload ho raha hai...', 'success');
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err) {
                        showToast('Cache clear failed: ' + (err instanceof Error ? err.message : 'Unknown'), 'error');
                      }
                    }}
                    className="w-full py-3 rounded-xl bg-red-50 text-red-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap border border-red-200"
                  >
                    <i className="ri-refresh-line text-lg"></i>
                    Clear Cache & Reload (Fresh Start)
                  </button>
                </div>
              </div>

              {/* ── DIRECT DELIVERY SETTINGS ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <i className="ri-shield-check-line text-purple-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Direct Delivery Mode</p>
                    <p className="text-gray-400 text-xs">Bina OTP ke order deliver karne ka option</p>
                  </div>
                </div>

                <div className={`rounded-2xl border p-4 transition-all ${directDeliveryEnabled ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${directDeliveryEnabled ? 'bg-purple-500' : 'bg-gray-300'}`}>
                        <i className={`ri-shield-check-line text-white text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-gray-900 font-bold text-sm">{directDeliveryEnabled ? 'Direct Delivery ON' : 'Direct Delivery OFF'}</p>
                        <p className={`text-xs ${directDeliveryEnabled ? 'text-purple-600' : 'text-gray-400'}`}>
                          {directDeliveryEnabled ? 'OTP skip karke direct deliver' : 'OTP verification required'}
                        </p>
                      </div>
                    </div>
                    {/* Toggle Switch */}
                    <button
                      onClick={() => {
                        const newValue = !directDeliveryEnabled;
                        setDirectDeliveryEnabled(newValue);
                        localStorage.setItem('direct_delivery_enabled', newValue.toString());
                        showToast(newValue ? 'Direct Delivery ON — Ab OTP nahi chahiye!' : 'Direct Delivery OFF — OTP required', newValue ? 'success' : 'info');
                      }}
                      className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${directDeliveryEnabled ? 'bg-purple-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${directDeliveryEnabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
                    </button>
                  </div>

                  {directDeliveryEnabled ? (
                    <div className="bg-purple-100 border border-purple-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-information-line text-purple-600 shrink-0 mt-0.5"></i>
                      <p className="text-purple-700 text-xs leading-relaxed">
                        <strong>Delivery boy ab bina OTP ke deliver kar sakta hai!</strong> Size swap aur payment step same rahega, bas OTP verification skip ho jaayega. Customer ke order status mein "Delivered" dikhega.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-100 border border-gray-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-shield-keyhole-line text-gray-500 shrink-0 mt-0.5"></i>
                      <p className="text-gray-600 text-xs leading-relaxed">
                        <strong>Normal mode:</strong> Delivery boy ko customer se 6-digit OTP lena hoga. OTP verify hone ke baad hi delivery complete hogi.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ── WHATSAPP ADMIN NOTIFICATIONS CARD ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <i className="ri-whatsapp-fill text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">WhatsApp Order Notifications</p>
                    <p className="text-gray-400 text-xs">Naya order aane pe WhatsApp pe message aayega</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Admin WhatsApp Number Input */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                      <i className="ri-whatsapp-fill text-green-500"></i>
                      Admin WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      value={adminWhatsAppNumber}
                      onChange={e => setAdminWhatsAppNumber(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                    />
                    <p className="text-gray-400 text-xs mt-1">Is number pe naya order aane pe WhatsApp message aayega</p>
                  </div>

                  {/* Save Button */}
                  {whatsAppSaved ? (
                    <div className="w-full py-3 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      WhatsApp Number Saved!
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        localStorage.setItem('admin_whatsapp_number', adminWhatsAppNumber);
                        setWhatsAppSaved(true);
                        showToast('WhatsApp number save ho gaya!', 'success');
                        setTimeout(() => setWhatsAppSaved(false), 3000);
                      }}
                      className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-save-line"></i>
                      Save WhatsApp Number
                    </button>
                  )}

                  {/* Last Notification Status */}
                  {lastWhatsAppNotif && (
                    <div className={`rounded-xl p-3 border ${lastWhatsAppNotif.status === 'sent' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <p className={`text-xs font-semibold flex items-center gap-1 ${lastWhatsAppNotif.status === 'sent' ? 'text-green-700' : 'text-red-700'}`}>
                        <i className={`${lastWhatsAppNotif.status === 'sent' ? 'ri-check-line text-green-500' : 'ri-close-line text-red-500'}`}></i>
                        Last WhatsApp: {lastWhatsAppNotif.status === 'sent' ? 'Sent' : 'Failed'} — Order #{lastWhatsAppNotif.orderId.slice(-6).toUpperCase()}
                      </p>
                      <p className="text-gray-400 text-xs mt-0.5">{new Date(lastWhatsAppNotif.time).toLocaleTimeString('en-IN')}</p>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-amber-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-information-line text-amber-500"></i>
                      Kaise Setup Karein:
                    </p>
                    <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                      <li>Apna WhatsApp number yahan daalo (with +91)</li>
                      <li>Supabase dashboard mein jaao → Edge Functions</li>
                      <li>Secrets add karo: WHATSAPP_API_TOKEN, WHATSAPP_PHONE_NUMBER_ID</li>
                      <li>Ab har naye order pe WhatsApp message aayega!</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* ── META PIXEL SETTINGS CARD ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <i className="ri-facebook-circle-fill text-blue-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Meta Pixel Settings</p>
                    <p className="text-gray-400 text-xs">Facebook/Instagram ads tracking setup karo</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Meta Pixel ID Input */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                      <i className="ri-barcode-fill text-blue-500"></i>
                      Meta Pixel ID
                    </label>
                    <input
                      type="text"
                      value={metaPixelId}
                      onChange={e => setMetaPixelId(e.target.value)}
                      placeholder="123456789012345"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                    />
                    <p className="text-gray-400 text-xs mt-1">Facebook Business Manager se Pixel ID copy karke yahan paste karo</p>
                  </div>

                  {/* Enable/Disable Toggle */}
                  <div className={`rounded-2xl border p-4 transition-all ${metaPixelEnabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${metaPixelEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}>
                          <i className="ri-eye-fill text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-gray-900 font-bold text-sm">Pixel Tracking</p>
                          <p className={`text-xs ${metaPixelEnabled ? 'text-blue-600' : 'text-gray-400'}`}>
                            {metaPixelEnabled ? 'Tracking active — events bheje ja rahe hain' : 'Tracking disabled'}
                          </p>
                        </div>
                      </div>
                      {/* Toggle Switch */}
                      <button
                        onClick={() => setMetaPixelEnabled(prev => !prev)}
                        className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${metaPixelEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${metaPixelEnabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  {metaPixelSaved ? (
                    <div className="w-full py-3 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      Pixel Settings Saved! Active now.
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        localStorage.setItem('meta_pixel_id', metaPixelId);
                        localStorage.setItem('meta_pixel_enabled', metaPixelEnabled.toString());
                        setMetaPixelSaved(true);
                        
                        // Re-initialize pixel without page reload
                        import('@/lib/metaPixel').then(({ reinitMetaPixel }) => {
                          reinitMetaPixel();
                        });
                        
                        showToast(metaPixelEnabled ? 'Meta Pixel enabled and active!' : 'Meta Pixel disabled', 'success');
                        
                        // Reset saved state after 3 seconds
                        setTimeout(() => {
                          setMetaPixelSaved(false);
                        }, 3000);
                      }}
                      className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-save-line"></i>
                      Save Pixel Settings
                    </button>
                  )}

                  {/* Test Pixel Button */}
                  {metaPixelEnabled && metaPixelId && (
                    <button
                      onClick={() => {
                        // Import and check pixel status
                        import('@/lib/metaPixel').then(({ isMetaPixelActive, trackPixelEvent }) => {
                          if (isMetaPixelActive()) {
                            // Send test Purchase event (more visible in Events Manager)
                            trackPixelEvent('Purchase', {
                              content_ids: ['TEST_PRODUCT_123'],
                              content_type: 'product',
                              value: 999,
                              currency: 'INR',
                              test_event: true,
                            });
                            showToast('Test Purchase event sent! Check Meta Events Manager.', 'success');
                          } else {
                            showToast('Pixel not active. Save settings first.', 'error');
                          }
                        });
                      }}
                      className="w-full py-3 rounded-xl bg-blue-50 text-blue-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-100 transition-all cursor-pointer whitespace-nowrap border border-blue-200"
                    >
                      <i className="ri-test-tube-line"></i>
                      Send Test Event
                    </button>
                  )}

                  {/* Info Box */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-blue-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-information-line text-blue-500"></i>
                      Kaise Kaam Karta Hai:
                    </p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>PageView:</strong> Har page load pe event bheja jaayega</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>ViewContent:</strong> Product detail open hone pe</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>AddToCart:</strong> Cart mein product add hone pe</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>InitiateCheckout:</strong> Order confirm start hone pe</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-blue-500 shrink-0"></i><strong>Purchase:</strong> Order successfully place hone pe</li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-amber-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-lightbulb-flash-line text-amber-500"></i>
                      Setup Steps:
                    </p>
                    <ol className="text-xs text-amber-700 space-y-1 list-decimal list-inside">
                      <li>Facebook Business Manager mein jaao</li>
                      <li>Events Manager → Data Sources → Pixels</li>
                      <li>Apna Pixel ID copy karo (15-digit number)</li>
                      <li>Yahan paste karo aur Save karo</li>
                      <li>Page refresh karo — tracking shuru!</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* ── CUSTOM HEAD CODE CARD ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center">
                    <i className="ri-code-s-slash-line text-white text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Custom Head Code</p>
                    <p className="text-gray-400 text-xs">Google Analytics, GTM, ya koi bhi &lt;head&gt; code yahan paste karo</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Info Box */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-amber-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-lightbulb-flash-line text-amber-500"></i>
                      Kya paste kar sakte ho:
                    </p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i><strong>Google Analytics 4</strong> — gtag.js script</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i><strong>Google Tag Manager</strong> — GTM head snippet</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i><strong>Meta Pixel</strong> — Facebook pixel base code</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i><strong>Hotjar, Clarity</strong> — heatmap tracking</li>
                      <li className="flex items-start gap-1"><i className="ri-check-line text-amber-500 shrink-0"></i><strong>Custom CSS/JS</strong> — koi bhi &lt;script&gt; ya &lt;style&gt; tag</li>
                    </ul>
                  </div>

                  {/* Code Textarea */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1">
                        <i className="ri-code-line text-gray-500"></i>
                        HTML Code (&lt;head&gt; mein jaayega)
                      </label>
                      <button
                        onClick={() => setCustomHeadPreview(p => !p)}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 cursor-pointer"
                      >
                        <i className={customHeadPreview ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                        {customHeadPreview ? 'Hide Preview' : 'Preview'}
                      </button>
                    </div>
                    <div className="relative">
                      <textarea
                        value={customHeadCode}
                        onChange={e => setCustomHeadCode(e.target.value)}
                        placeholder={`<!-- Google Analytics 4 example -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>\n<script>\n  window.dataLayer = window.dataLayer || [];\n  function gtag(){dataLayer.push(arguments);}\n  gtag('js', new Date());\n  gtag('config', 'G-XXXXXXXXXX');\n</script>`}
                        rows={10}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-xs font-mono focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none resize-y bg-gray-50 text-gray-800 leading-relaxed"
                        spellCheck={false}
                      />
                      {customHeadCode && (
                        <button
                          onClick={() => setCustomHeadCode('')}
                          className="absolute top-2 right-2 w-6 h-6 rounded-lg bg-gray-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center text-gray-500 transition-all cursor-pointer"
                          title="Clear"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-gray-400 text-xs">Yeh code website ke &lt;head&gt; tag mein inject hoga — page load pe automatically</p>
                      <span className="text-gray-400 text-xs font-mono">{customHeadCode.length} chars</span>
                    </div>
                  </div>

                  {/* Preview */}
                  {customHeadPreview && customHeadCode && (
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-700">
                      <p className="text-gray-400 text-xs font-semibold mb-2 flex items-center gap-1">
                        <i className="ri-eye-line text-gray-500"></i>
                        Preview (read-only)
                      </p>
                      <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap break-all leading-relaxed overflow-auto max-h-48">
                        {customHeadCode}
                      </pre>
                    </div>
                  )}

                  {/* Character count warning */}
                  {customHeadCode.length > 5000 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                      <i className="ri-error-warning-line text-red-500 shrink-0 mt-0.5"></i>
                      <p className="text-red-700 text-xs">Code bahut lamba hai ({customHeadCode.length} chars). Performance affect ho sakti hai.</p>
                    </div>
                  )}

                  {/* Save Button */}
                  {customHeadSaved ? (
                    <div className="w-full py-3 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      Code Saved &amp; Injected! Page reload pe active hoga.
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        import('@/lib/customHeadCode').then(({ saveCustomHeadCode, reinjectCustomHeadCode }) => {
                          saveCustomHeadCode(customHeadCode);
                          reinjectCustomHeadCode();
                          setCustomHeadSaved(true);
                          showToast(
                            customHeadCode.trim()
                              ? 'Custom head code saved & injected!'
                              : 'Custom head code cleared!',
                            'success'
                          );
                          setTimeout(() => setCustomHeadSaved(false), 3000);
                        });
                      }}
                      className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-save-line"></i>
                      Save &amp; Inject Code
                    </button>
                  )}

                  {/* Clear Button */}
                  {customHeadCode && (
                    <button
                      onClick={() => {
                        setCustomHeadCode('');
                        import('@/lib/customHeadCode').then(({ saveCustomHeadCode, reinjectCustomHeadCode }) => {
                          saveCustomHeadCode('');
                          reinjectCustomHeadCode();
                          showToast('Custom head code remove ho gaya!', 'info');
                        });
                      }}
                      className="w-full py-3 rounded-xl bg-red-50 text-red-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-red-100 transition-all cursor-pointer whitespace-nowrap border border-red-200"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Remove All Custom Code
                    </button>
                  )}

                  {/* Warning */}
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                    <p className="text-red-800 text-xs font-semibold mb-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-red-500"></i>
                      Important Warning:
                    </p>
                    <p className="text-red-700 text-xs leading-relaxed">
                      Sirf <strong>trusted sources</strong> ka code paste karo (Google, Meta, etc.). 
                      Unknown ya suspicious code se website hack ho sakti hai. 
                      <strong>Galat code website tod sakta hai!</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* ── CONTACT SETTINGS CARD ── */}
              <div className="bg-white rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-green-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-base">Customer Support Settings</p>
                    <p className="text-gray-400 text-xs">Phone, Instagram aur WhatsApp button manage karo</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Phone Number */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                      <i className="ri-phone-fill text-orange-500"></i>
                      Phone / WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      value={contactSettings.phone}
                      onChange={e => setContactSettings(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                    />
                    <p className="text-gray-400 text-xs mt-1">Yeh number WhatsApp aur Call button dono mein use hoga</p>
                  </div>

                  {/* Instagram ID */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide flex items-center gap-1">
                      <i className="ri-instagram-fill text-rose-500"></i>
                      Instagram ID
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">@</span>
                      <input
                        type="text"
                        value={contactSettings.instagramId.replace('@', '')}
                        onChange={e => setContactSettings(prev => ({ ...prev, instagramId: '@' + e.target.value.replace('@', '') }))}
                        placeholder="clothybazar"
                        className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none"
                      />
                    </div>
                    <p className="text-gray-400 text-xs mt-1">Contact section mein dikhega</p>
                  </div>

                  {/* WhatsApp On/Off Toggle */}
                  <div className={`rounded-2xl border p-4 transition-all ${contactSettings.whatsappEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${contactSettings.whatsappEnabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                          <i className="ri-whatsapp-fill text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-gray-900 font-bold text-sm">WhatsApp Button</p>
                          <p className={`text-xs ${contactSettings.whatsappEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                            {contactSettings.whatsappEnabled ? 'WhatsApp button visible hai customers ko' : 'WhatsApp button hidden hai'}
                          </p>
                        </div>
                      </div>
                      {/* Toggle Switch */}
                      <button
                        onClick={() => setContactSettings(prev => ({ ...prev, whatsappEnabled: !prev.whatsappEnabled }))}
                        className={`relative w-12 h-6 rounded-full transition-all cursor-pointer shrink-0 ${contactSettings.whatsappEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${contactSettings.whatsappEnabled ? 'translate-x-6' : 'translate-x-0'}`}></span>
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  {contactSaved ? (
                    <div className="w-full py-3 rounded-xl bg-green-100 border border-green-300 text-green-700 font-bold text-sm flex items-center justify-center gap-2">
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      Settings Saved!
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        saveContactSettings(contactSettings);
                        setContactSaved(true);
                        showToast('Contact settings save ho gaye!', 'success');
                        setTimeout(() => setContactSaved(false), 3000);
                      }}
                      className="w-full py-3 rounded-xl bg-gray-900 text-white font-bold text-sm flex items-center justify-center gap-2 hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-save-line"></i>
                      Save Contact Settings
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── HISTORY PANEL ── */}
          {mainTab === 'history' && (
            <>
              {/* Date Filter for History */}
              <div className="bg-white rounded-2xl border border-gray-100 p-3 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                    <i className="ri-calendar-event-line text-gray-600 text-sm"></i>
                  </div>
                  <p className="text-gray-700 font-bold text-sm">Date Filter</p>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {DATE_TABS.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => handleDateChange(tab.key)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                        datePeriod === tab.key
                          ? 'bg-gray-900 text-white'
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100'
                      }`}
                    >
                      <i className={`${tab.icon} text-base`}></i>
                      <span className="leading-none text-center">{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-4">
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setHistoryTab('shipped')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      historyTab === 'shipped' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-truck-line"></i>
                    Shipped
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${historyTab === 'shipped' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {counts.shipped}
                    </span>
                  </button>
                  <button
                    onClick={() => setHistoryTab('delivered')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      historyTab === 'delivered' ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-check-double-line"></i>
                    Delivered
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${historyTab === 'delivered' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {counts.delivered}
                    </span>
                  </button>
                  <button
                    onClick={() => setHistoryTab('cancelled')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                      historyTab === 'cancelled' ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className="ri-close-circle-line"></i>
                    Cancelled
                    <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${historyTab === 'cancelled' ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {counts.cancelled}
                    </span>
                  </button>
                  <div className="flex-1 hidden sm:block"></div>
                  <div className="relative hidden sm:block">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input
                      type="text"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search..."
                      className="pl-8 pr-3 py-2 rounded-xl border border-gray-200 text-xs focus:border-gray-400 outline-none w-40"
                    />
                  </div>
                </div>

                {historyTab === 'delivered' && historyOrders.length > 0 && (
                  <div className="flex items-center justify-between bg-green-50 border border-green-100 rounded-xl px-3 py-2 mb-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-money-rupee-circle-line text-green-500"></i>
                      <span className="text-green-700 text-xs font-semibold">Revenue ({periodLabel})</span>
                    </div>
                    <span className="text-green-800 font-bold text-sm">₹{periodRevenue.toLocaleString('en-IN')}</span>
                  </div>
                )}

                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>
                    Showing {historyOrders.length} of {historyTotal} {historyTab} orders
                    {search ? ` · filtered: ${filteredHistory.length}` : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-information-line"></i>
                    {PAGE_SIZE} per page
                  </span>
                </div>
              </div>

              {historyLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-gray-900 animate-spin"></div>
                  <p className="text-gray-500 text-sm">Loading {historyTab} orders...</p>
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-4 bg-white rounded-2xl border border-gray-100">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${historyTab === 'delivered' ? 'bg-green-50' : 'bg-red-50'}`}>
                    <i className={`text-3xl ${historyTab === 'delivered' ? 'ri-check-double-line text-green-400' : 'ri-close-circle-line text-red-400'}`}></i>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-700 font-semibold">No {historyTab} orders</p>
                    <p className="text-gray-400 text-sm mt-1">
                      {search ? 'No orders match your search' : `No ${historyTab} orders for ${periodLabel.toLowerCase()}`}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {filteredHistory.map(order => (
                      <OrderCard key={order.id} order={order} onStatusChange={handleStatusChange} />
                    ))}
                  </div>

                  {historyHasMore && !search && (
                    <div className="mt-4 flex flex-col items-center gap-2">
                      <button
                        onClick={() => loadHistory(historyTab, historyPage + 1, true)}
                        disabled={historyLoadingMore}
                        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {historyLoadingMore ? (
                          <><i className="ri-loader-4-line animate-spin"></i>Loading...</>
                        ) : (
                          <><i className="ri-arrow-down-line"></i>Load More ({Math.min(PAGE_SIZE, historyTotal - historyOrders.length)} more)</>
                        )}
                      </button>
                      <p className="text-xs text-gray-400">{historyOrders.length} of {historyTotal} loaded</p>
                    </div>
                  )}

                  {!historyHasMore && historyOrders.length > 0 && !search && (
                    <div className="mt-4 text-center">
                      <p className="text-xs text-gray-400 flex items-center justify-center gap-1">
                        <i className="ri-check-line text-green-500"></i>
                        All {historyTotal} {historyTab} orders loaded
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}

        </div>
        {/* end main content */}
      </div>
      {/* end flex wrapper */}
    </div>
  );
}
